// Renders an array of ContentBlock. The AI builder in Phase 3 generates
// arrays of these to deliver slide-deck-quality pages per assessment.

import { Fragment, useEffect, useRef, useState } from 'react';
import { useBrand } from './BrandContext';
import { usePhase } from './PhaseContext';
import { HeroBackgroundLayer } from './HeroBackground';
import type {
  ContentBlock,
  HeadingBlock, ParagraphBlock, ImageBlock, CalloutBlock, QuoteBlock,
  StatBlock, StatsGridBlock, ListBlock, DiagramBlock, VideoBlock,
  CtaBlock, IconBlock, SpacerBlock, ColumnsBlock, CardBlock, HtmlBlock,
  HeroBlock, MoneyMathBlock, TickerBlock, PillarCardsBlock, ProofQuotesBlock,
  RegistrationStatusCardBlock,
  CountdownBlock, FaqBlock, HostBioBlock, ReplayVideoBlock,
  EventHeroBlock, ReplayWindowPillBlock, VideoFrameBlock, HighlightReelBlock,
  ChatPullQuotesBlock, NextCohortCalloutBlock, FinalCtaBlock, AttendeeStripBlock,
  AnnounceBarBlock, StepsListBlock, PerksListBlock, InlineWaitlistFormBlock,
  InlineRegistrationFormBlock, SiteNavBlock, SectionBlock, SplitFeatureBlock,
  PricingTiersBlock, PricingTierCard,
  ScriptEmbedBlock, QuoteBandBlock, DividerBlock,
  AddToCalendarBlock, BeltLadderBlock, ReasonsListBlock, EventFooterBlock,
} from '../types';
import { BELTS } from '../lib/belt';
import { POLICY_LINKS, CONTACT_URL } from './PolicyLinks';
import { buildCalendarLinks } from '../lib/calendar';
import { fetchGeoCountry, resolveEventCurrency } from '../lib/geoCurrency';
import { announceWindowOpen } from '../lib/announceWindow';
import { BgProvider, toneFor, type SectionBackground } from './SectionContext';
import { getAttributionPayload } from '../attribution';

export function Blocks({ blocks }: { blocks?: ContentBlock[] }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="blocks">
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}

export function Block({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'section':    return <SectionRender b={block as unknown as SectionBlock} />;
    case 'heading':    return <HeadingRender b={block} />;
    case 'paragraph':  return <ParagraphRender b={block} />;
    case 'image':      return <ImageRender b={block} />;
    case 'callout':    return <CalloutRender b={block} />;
    case 'quote':      return <QuoteRender b={block} />;
    case 'stat':       return <StatRender b={block} />;
    case 'statsGrid':  return <StatsGridRender b={block} />;
    case 'list':       return <ListRender b={block} />;
    case 'diagram':    return <DiagramRender b={block} />;
    case 'video':      return <VideoRender b={block} />;
    case 'cta':        return <CtaRender b={block} />;
    case 'icon':       return <IconRender b={block} />;
    case 'divider':    return <DividerRender b={block as unknown as DividerBlock} />;
    case 'spacer':     return <SpacerRender b={block} />;
    case 'columns':    return <ColumnsRender b={block} />;
    case 'card':       return <CardRender b={block} />;
    case 'html':       return <HtmlRender b={block} />;
    case 'hero':       return <HeroRender b={block as unknown as HeroBlock} />;
    case 'moneyMath':  return <MoneyMathRender b={block as unknown as MoneyMathBlock} />;
    case 'ticker':     return <TickerRender b={block as unknown as TickerBlock} />;
    case 'pillarCards':return <PillarCardsRender b={block as unknown as PillarCardsBlock} />;
    case 'proofQuotes':return <ProofQuotesRender b={block as unknown as ProofQuotesBlock} />;
    case 'registrationStatusCard':
      return (block as unknown as RegistrationStatusCardBlock).variant === 'letter'
        ? <RegistrationLetterRender b={block as unknown as RegistrationStatusCardBlock} />
        : <RegistrationStatusCardRender b={block as unknown as RegistrationStatusCardBlock} />;
    case 'countdown':  return <CountdownRender b={block as unknown as CountdownBlock} />;
    case 'faq':        return <FaqRender b={block as unknown as FaqBlock} />;
    case 'hostBio':    return <HostBioRender b={block as unknown as HostBioBlock} />;
    case 'replayVideo':return <ReplayVideoRender b={block as unknown as ReplayVideoBlock} />;
    // ── TSB event-landing primitives (Path A) ────────────────────────────
    case 'eventHero':        return <EventHeroRender b={block as unknown as EventHeroBlock} />;
    case 'replayWindowPill': return <ReplayWindowPillRender b={block as unknown as ReplayWindowPillBlock} />;
    case 'videoFrame':       return <VideoFrameRender b={block as unknown as VideoFrameBlock} />;
    case 'highlightReel':    return <HighlightReelRender b={block as unknown as HighlightReelBlock} />;
    case 'chatPullQuotes':   return <ChatPullQuotesRender b={block as unknown as ChatPullQuotesBlock} />;
    case 'nextCohortCallout':return <NextCohortCalloutRender b={block as unknown as NextCohortCalloutBlock} />;
    case 'finalCta':         return <FinalCtaRender b={block as unknown as FinalCtaBlock} />;
    case 'attendeeStrip':    return <AttendeeStripRender b={block as unknown as AttendeeStripBlock} />;
    case 'announceBar':      return <AnnounceBarRender b={block as unknown as AnnounceBarBlock} />;
    case 'stepsList':        return <StepsListRender b={block as unknown as StepsListBlock} />;
    case 'perksList':        return <PerksListRender b={block as unknown as PerksListBlock} />;
    case 'inlineWaitlistForm': return <InlineWaitlistFormRender b={block as unknown as InlineWaitlistFormBlock} />;
    case 'inlineRegistrationForm':
      return <InlineRegistrationFormRender b={block as unknown as InlineRegistrationFormBlock} />;
    case 'pricingTiers': return <PricingTiersRender b={block as unknown as PricingTiersBlock} />;
    case 'siteNav':    return <SiteNavRender b={block as unknown as SiteNavBlock} />;
    case 'splitFeature': return <SplitFeatureRender b={block as unknown as SplitFeatureBlock} />;
    case 'scriptEmbed': return <ScriptEmbedRender b={block as unknown as ScriptEmbedBlock} />;
    case 'quoteBand':  return <QuoteBandRender b={block as unknown as QuoteBandBlock} />;
    case 'addToCalendar': return <AddToCalendarRender b={block as unknown as AddToCalendarBlock} />;
    case 'beltLadder':    return <BeltLadderRender b={block as unknown as BeltLadderBlock} />;
    case 'reasonsList':   return <ReasonsListRender b={block as unknown as ReasonsListBlock} />;
    case 'eventFooter':   return <EventFooterRender b={block as unknown as EventFooterBlock} />;
    default:           return null;
  }
}

// =========================================================================
// SECTION PRIMITIVE — the unification layer (Layer 1).
// A full-bleed band that owns its background and broadcasts a dark/light tone
// to its children. Child block color adaptation is pure CSS, keyed off the
// `data-bg` attribute (see sections.css); the BgProvider is for the rare block
// that swaps markup by tone. CSS owns the full-bleed breakout + inner max-width
// so configs never hand-roll negative-margin hacks again.
// =========================================================================
const SECTION_PAD: Record<NonNullable<SectionBlock['padding']>, string> = {
  none: '0', sm: '40px', md: '56px', lg: '72px', xl: '96px',
};

function SectionRender({ b }: { b: SectionBlock }) {
  const bg = (b.background ?? 'white') as SectionBackground;
  const tone = toneFor(bg);
  const pad = SECTION_PAD[b.padding ?? 'lg'];
  const innerStyle: React.CSSProperties = {};
  if (b.maxWidth != null) innerStyle.maxWidth = `${b.maxWidth}px`;
  return (
    <section className={`sc-section sc-bg-${bg}${b.centered ? ' sc-centered' : ''}`} data-bg={tone}>
      <div
        className="sc-section-inner"
        style={{ paddingTop: pad, paddingBottom: pad, ...innerStyle }}
      >
        <BgProvider tone={tone}>
          {(b.blocks ?? []).map((inner, i) => <Block key={i} block={inner} />)}
        </BgProvider>
      </div>
    </section>
  );
}

// =========================================================================
// TSB EVENT-LANDING PRIMITIVES — render the design-system-locked vocabulary
// from /landings/cbl-staging.html. CSS in taker/src/blocks-events.css.
//
// Helper: inline-format markers — {{em:phrase}} → <em>phrase</em>,
// {{strong:phrase}} → <strong>phrase</strong>. Authors use these in headlines
// and bodies to apply coral/gold italic emphasis or navy bold without writing
// raw HTML. dangerouslySetInnerHTML is acceptable here because the template
// is authored in trusted admin tooling, not user-submitted content.
// =========================================================================
function formatTsbInline(s: string): string {
  return (s ?? '')
    .replace(/\{\{em:(.+?)\}\}/g, '<em>$1</em>')
    .replace(/\{\{strong:(.+?)\}\}/g, '<strong>$1</strong>')
    .replace(/\{\{grad:(.+?)\}\}/g, '<span class="tsb-grad">$1</span>');
}

function DividerRender({ b }: { b: DividerBlock }) {
  if (b?.variant === 'gradient-angle') {
    return <div className="block-divider-gradient-angle" aria-hidden="true" />;
  }
  return <hr className="block-divider" />;
}

// ScriptEmbedRender — appends the third-party <script> via the DOM API so it
// actually executes (dangerouslySetInnerHTML never runs <script> content).
// Idempotent per mountId so React StrictMode's double-invoke doesn't double-load.
const scriptEmbedLoaded = new Set<string>();
function ScriptEmbedRender({ b }: { b: ScriptEmbedBlock }) {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scriptEmbedLoaded.has(b.mountId)) return;
    scriptEmbedLoaded.add(b.mountId);
    const script = document.createElement('script');
    script.src = b.src;
    script.async = b.async ?? true;
    script.defer = b.defer ?? true;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.src, b.mountId]);

  return (
    <section className="tsb-evt-scope tsb-evt-section">
      <div className="tsb-evt-container-wide">
        <div
          ref={mountRef}
          id={b.mountId}
          className="tsb-evt-script-embed"
          style={b.minHeight ? { minHeight: b.minHeight } : undefined}
        />
      </div>
    </section>
  );
}

function QuoteBandRender({ b }: { b: QuoteBandBlock }) {
  return (
    <div
      className={`tsb-evt-scope tsb-evt-quoteband align-${b.align ?? 'right'}`}
      style={{ backgroundImage: `url(${b.imageUrl})` }}
    >
      <div className="tsb-evt-container">
        <div className="tsb-evt-quoteband-card">
          <h3>{b.heading}</h3>
          <p>&ldquo;{b.quote}&rdquo;</p>
          {b.attribution && <div className="tsb-evt-quoteband-attr">{b.attribution}</div>}
        </div>
      </div>
    </div>
  );
}

function EventHeroRender({ b }: { b: EventHeroBlock }) {
  const theme = b.theme ?? 'navy';
  const palette = b.blobPalette ?? (theme === 'navy' ? 'coral-gold' : 'coral-gold');
  // Which registration modal is open, if any, and which config drives it.
  // A single slot rather than two booleans — only one of the two CTAs can
  // realistically be clicked at a time, and this makes that structurally true
  // rather than merely conventional.
  const [openModal, setOpenModal] = useState<null | {
    mode: 'replay' | 'pre-event';
    heading?: string;
    body?: string;
    submitLabel?: string;
  }>(null);

  function handlePrimaryClick(e: React.MouseEvent) {
    if (b.cta?.action !== 'register') return; // default 'link'/absent: let the anchor navigate
    e.preventDefault();
    setOpenModal({
      mode: 'pre-event',
      heading: b.cta.modalHeading,
      body: b.cta.modalBody,
      submitLabel: b.cta.modalSubmitLabel,
    });
  }

  function handleSecondaryClick(e: React.MouseEvent) {
    if (!b.secondaryCta) return;
    if (b.secondaryCta.action === 'late-register') {
      e.preventDefault();
      setOpenModal({
        mode: 'replay',
        heading: b.secondaryCta.modalHeading,
        body: b.secondaryCta.modalBody,
        submitLabel: b.secondaryCta.modalSubmitLabel,
      });
    }
    // 'link' action: let the anchor navigate naturally
  }

  const layout = b.layout ?? 'centered';

  return (
    <section className={`tsb-evt-scope tsb-evt-hero theme-${theme} palette-${palette} layout-${layout}`}>
      {theme === 'navy' && (
        // A configured background (animated 'network' canvas, video, gradient)
        // replaces the CSS blobs. The section is already position:relative;
        // overflow:hidden and .tsb-evt-hero-inner sits at z-index 2, so the
        // background layer (z 0–1) paints behind the content. 'image' backgrounds
        // are a CSS-shell concern and no-op through HeroBackgroundLayer, so we
        // keep the blobs for that case.
        b.background && b.background.type !== 'image' ? (
          <HeroBackgroundLayer bg={b.background} />
        ) : (
          <div className="tsb-evt-hero-blobs" aria-hidden="true">
            <div className="tsb-evt-hero-blob b1" />
            <div className="tsb-evt-hero-blob b2" />
            <div className="tsb-evt-hero-blob b3" />
          </div>
        )
      )}
      <div className="tsb-evt-hero-inner">
        <div className={layout === 'split' ? 'tsb-evt-hero-copy' : undefined}>
        {b.badge && (
          <div className={`tsb-evt-hero-badge variant-${b.badge.variant ?? 'coral'}`}>
            <span className="tsb-evt-hero-badge-dot" />
            {b.badge.text}
          </div>
        )}
        {b.eyebrow && <div className="tsb-evt-hero-eyebrow">{b.eyebrow}</div>}
        <h1 dangerouslySetInnerHTML={{ __html: formatTsbInline(b.headline) }} />
        {b.subhead && (
          <p
            className="tsb-evt-hero-sub"
            dangerouslySetInnerHTML={{ __html: formatTsbInline(b.subhead) }}
          />
        )}
        {b.metaRow && b.metaRow.length > 0 && (
          <div className="tsb-evt-hero-meta">
            {b.metaRow.map((item, i) => (
              <Fragment key={i}>
                {/* A dotted item carries its own marker — a "·" before it reads as two. */}
                {i > 0 && !item.dot && <span className="tsb-evt-hero-meta-sep" aria-hidden="true">·</span>}
                <span className={`tsb-evt-hero-meta-item${item.strong ? ' is-strong' : ''}`}>
                  {item.dot && <span className="tsb-evt-hero-meta-dot" aria-hidden="true" />}
                  {item.text}
                </span>
              </Fragment>
            ))}
          </div>
        )}
        {b.eventStrip && b.eventStrip.length > 0 && (
          <div className="tsb-evt-strip">
            {b.eventStrip.map((cell, i) => (
              <div key={i} className="tsb-evt-strip-cell">
                <span className="tsb-evt-strip-label">{cell.label}</span>
                <span className={`tsb-evt-strip-value${cell.accent ? ' accent' : ''}`}>{cell.value}</span>
              </div>
            ))}
          </div>
        )}
        {b.replayWindow && (
          <div className="tsb-evt-replay-pill">
            <div className="tsb-evt-replay-pill-icon">{b.replayWindow.icon ?? '⏱'}</div>
            <div className="tsb-evt-replay-pill-text">
              <div className="tsb-evt-replay-pill-label">{b.replayWindow.label ?? 'Replay window'}</div>
              <div
                className="tsb-evt-replay-pill-time"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.replayWindow.text) }}
              />
            </div>
          </div>
        )}
        {b.cta && (
          <div className="tsb-evt-hero-cta">
            {/* When a secondaryCta is present, render the two side-by-side. Otherwise
                preserve the original single-CTA layout that other phases rely on. */}
            {b.secondaryCta ? (
              <div className="tsb-evt-hero-cta-row">
                <a href={b.cta.url} onClick={handlePrimaryClick} className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-btn-lg">{b.cta.label}</a>
                <a
                  href={b.secondaryCta.action === 'link' ? (b.secondaryCta.url ?? '#') : '#'}
                  onClick={handleSecondaryClick}
                  className="tsb-evt-btn tsb-evt-btn-outline tsb-evt-btn-stacked"
                >
                  <span className="tsb-evt-btn-lead">{b.secondaryCta.leadLabel}</span>
                  {b.secondaryCta.subLabel && (
                    <span className="tsb-evt-btn-sub">{b.secondaryCta.subLabel}</span>
                  )}
                </a>
              </div>
            ) : (
              <a href={b.cta.url} onClick={handlePrimaryClick} className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-btn-lg">{b.cta.label}</a>
            )}
            {b.scarcity && <div className="tsb-evt-hero-scarcity">{b.scarcity}</div>}
            {b.liveCounter && (
              <div className="tsb-evt-hero-counter">
                <span className="tsb-evt-hero-counter-dot" />
                {b.liveCounter.count > 0
                  ? `${b.liveCounter.count.toLocaleString()} ${b.liveCounter.label}`
                  : b.liveCounter.label}
              </div>
            )}
          </div>
        )}
        </div>
        {layout === 'split' && b.media && (
          <div className="tsb-evt-hero-media">
            <img
              className="tsb-evt-hero-media-img"
              src={b.media.imageUrl}
              alt={b.media.alt ?? ''}
              loading="eager"
            />
            {b.countdown && <HeroCountdown c={b.countdown} />}
          </div>
        )}
      </div>

      {openModal && (
        <LateRegisterModal
          mode={openModal.mode}
          heading={openModal.heading}
          body={openModal.body}
          submitLabel={openModal.submitLabel}
          onClose={() => setOpenModal(null)}
        />
      )}
    </section>
  );
}

/** splitFeature — copy + bullets left, numbered thematic card right. */
function SplitFeatureRender({ b }: { b: SplitFeatureBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section tsb-evt-splitfeature">
      <div className="tsb-evt-container-wide">
        <div className="tsb-evt-splitfeature-copy">
          {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
          {b.heading && (
            <h2
              className="tsb-evt-section-heading"
              dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
            />
          )}
          {b.body && (
            <p
              className="tsb-evt-section-intro"
              dangerouslySetInnerHTML={{ __html: formatTsbInline(b.body) }}
            />
          )}
          {b.bullets && b.bullets.length > 0 && (
            <ul className="tsb-evt-splitfeature-bullets">
              {b.bullets.map((it, i) => <li key={i}>{it}</li>)}
            </ul>
          )}
        </div>
        {b.card && (
          <div className="tsb-evt-splitfeature-card">
            <div className="tsb-evt-splitfeature-card-head">
              <h3 className="tsb-evt-splitfeature-card-title">{b.card.title}</h3>
              {b.card.meta && <span className="tsb-evt-splitfeature-card-meta">{b.card.meta}</span>}
            </div>
            <div className="tsb-evt-splitfeature-rows">
              {b.card.rows.map((row, i) => (
                <div key={i} className="tsb-evt-splitfeature-row">
                  <span className="tsb-evt-splitfeature-num">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <p className="tsb-evt-splitfeature-row-title">{row.title}</p>
                    {row.body && <p className="tsb-evt-splitfeature-row-body">{row.body}</p>}
                  </div>
                </div>
              ))}
            </div>
            {b.card.footnote && (
              <p className="tsb-evt-splitfeature-footnote">{b.card.footnote}</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** proofQuotes variant='carousel' — one quote at a time, with a numbered
 *  name rail and prev/next controls. State is a plain index; the rail
 *  buttons are real <button>s so keyboard and screen-reader users get the
 *  same control as a pointer. No autoplay: an auto-advancing testimonial
 *  steals reading time from someone mid-sentence. */
function ProofCarousel({ b }: { b: ProofQuotesBlock }) {
  const items = b.quotes ?? [];
  const [active, setActive] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  // Keep the active name visible when the rail holds more entries than it
  // shows (the Summit rail windows ~5 of 30). `block: 'nearest'` scrolls the
  // rail only, never the page; and NO `behavior: 'smooth'` — smooth scrolling
  // is animation-driven and never progresses in a hidden/background tab.
  useEffect(() => {
    railRef.current?.querySelector<HTMLElement>('.is-active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);
  if (items.length === 0) return null;
  const current = items[Math.min(active, items.length - 1)];
  const go = (delta: number) => setActive((i) => (i + delta + items.length) % items.length);

  return (
    <section className="tsb-evt-scope tsb-evt-section tsb-evt-proof-section" id={b.anchorId}>
      <div className="tsb-evt-container-wide">
        <div className="tsb-evt-proof-carousel">
          <div className="tsb-evt-proof-carousel-head">
            <div>
              {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
              {b.heading && (
                <h2
                  className="tsb-evt-section-heading"
                  dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
                />
              )}
            </div>
            {items.length > 1 && (
              <div className="tsb-evt-proof-nav">
                <span className="tsb-evt-proof-count">
                  {String(active + 1).padStart(2, '0')}
                  <span> / {String(items.length).padStart(2, '0')}</span>
                </span>
                <button
                  type="button"
                  className="tsb-evt-proof-nav-btn"
                  onClick={() => go(-1)}
                  aria-label="Previous testimonial"
                >‹</button>
                <button
                  type="button"
                  className="tsb-evt-proof-nav-btn"
                  onClick={() => go(1)}
                  aria-label="Next testimonial"
                >›</button>
              </div>
            )}
          </div>
          <div className="tsb-evt-proof-carousel-body">
            <div className="tsb-evt-proof-slide" aria-live="polite">
              {current.badge && (
                <div className="tsb-evt-proof-slide-result">{current.badge}</div>
              )}
              <blockquote>&ldquo;{current.quote}&rdquo;</blockquote>
              <div className="tsb-evt-proof-slide-attr">
                {current.avatarUrl ? (
                  <img
                    src={current.avatarUrl}
                    alt={current.name}
                    className="tsb-evt-proof-slide-avatar"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="tsb-evt-proof-slide-avatar tsb-evt-proof-slide-avatar-mono" aria-hidden="true">
                    {testimonialInitials(current.name)}
                  </span>
                )}
                <span>
                  <strong>{current.name}</strong>
                  {current.company && <> · {current.company}</>}
                </span>
              </div>
            </div>
            {items.length > 1 && (
              <div className="tsb-evt-proof-rail" ref={railRef}>
                {items.map((q, i) => (
                  <button
                    type="button"
                    key={i}
                    className={`tsb-evt-proof-rail-item${i === active ? ' is-active' : ''}`}
                    onClick={() => setActive(i)}
                    aria-current={i === active}
                  >
                    <span className="tsb-evt-proof-rail-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="tsb-evt-proof-rail-name">{q.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Countdown card overlaid on the split hero's media panel. Shares
 *  useCountdown with the standalone `countdown` block, so the timer logic
 *  exists once. Renders nothing once the target has passed — the hero has a
 *  live event happening at that point and a row of zeroes reads as broken. */
function HeroCountdown({ c }: { c: NonNullable<EventHeroBlock['countdown']> }) {
  const t = useCountdown(c.target, c.hideSeconds);
  if (!t || t.expired) return null;
  return (
    <div className="tsb-evt-hero-countdown">
      <div className="tsb-evt-hero-countdown-label">{c.label ?? 'Broadcast begins in'}</div>
      <div className="tsb-evt-hero-countdown-tiles" role="timer" aria-live="off">
        <HeroCountdownTile value={String(t.days)} unit="days" />
        <HeroCountdownTile value={pad2(t.hours)} unit="hours" />
        <HeroCountdownTile value={pad2(t.minutes)} unit="minutes" />
        {!c.hideSeconds && <HeroCountdownTile value={pad2(t.seconds)} unit="seconds" isSeconds />}
      </div>
      {c.caption && <div className="tsb-evt-hero-countdown-caption">{c.caption}</div>}
    </div>
  );
}

function HeroCountdownTile({ value, unit, isSeconds }: { value: string; unit: string; isSeconds?: boolean }) {
  return (
    <div className={`tsb-evt-hero-countdown-tile${isSeconds ? ' is-seconds' : ''}`}>
      <div className="tsb-evt-hero-countdown-value">{value}</div>
      <div className="tsb-evt-hero-countdown-unit">{unit}</div>
    </div>
  );
}

/** Two-step (email, then name-if-missing) inline registration modal.
 *  Two call sites, distinguished by `mode`:
 *   - 'replay' (default, original use): opens from the EventHero secondary
 *     CTA on replay-phase landings. On success, builds a CUSTOM redirect to
 *     /replays/:slug?email=... — the server's own redirectUrl/resumeUrl point
 *     at the take page, which bounces to a bare /replays gate in replay phase
 *     (dropping the email) and strands the just-registered visitor. Built for
 *     that one broken case; do not reuse this branch elsewhere.
 *   - 'pre-event': opens from the EventHero PRIMARY CTA before the event has
 *     happened. On success, navigates to the server's OWN redirectUrl/
 *     resumeUrl exactly as returned — no custom URL-building needed, because
 *     pre-event that response already resolves to the correct 'gate' phase
 *     (GatePage's confirmation + prep-survey CTA), unlike the replay case. */
function LateRegisterModal({
  heading,
  body,
  submitLabel,
  onClose,
  mode = 'replay',
}: {
  heading?: string;
  body?: string;
  submitLabel?: string;
  onClose: () => void;
  mode?: 'replay' | 'pre-event';
}) {
  const initialPrefill = readUrlPrefill();
  const [step, setStep] = useState<'step1' | 'step2'>('step1');
  const [email, setEmail] = useState(initialPrefill.email);
  const [firstName, setFirstName] = useState(initialPrefill.firstName);
  const [lastName, setLastName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step2Data, setStep2Data] = useState<{
    token: string;
    redirectUrl: string;
    knownFirstName: string | null;
    needsFirstName: boolean;
    needsLastName: boolean;
  } | null>(null);

  // Close on Esc + lock background scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // 'replay' builds its own redirect (see the docstring above for why);
  // 'pre-event' just trusts whatever URL the server itself hands back.
  const replayRedirectUrl = (trimmedEmail: string) =>
    `/replays/${encodeURIComponent(deriveSlugFromUrl() ?? '')}?email=${encodeURIComponent(trimmedEmail)}&source=lp-late-register`;
  const source = mode === 'pre-event' ? 'lp-hero-register' : 'lp-late-register';

  async function handleStep1(e: React.FormEvent) {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    const slug = deriveSlugFromUrl();
    if (!slug) {
      setError("Couldn't resolve the event from the URL. Please refresh and try again.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/embed/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, email: trimmedEmail, source, attribution: getAttributionPayload() }),
      });
      const data = await res.json().catch(() => ({})) as Record<string, unknown>;
      if (!res.ok) {
        const reason = (data as { reason?: string }).reason;
        setError(reason && reason !== 'invalid_format'
          ? "That email domain doesn't accept mail — please double-check it."
          : ((data as { error?: string }).error ?? 'Something went wrong. Try again in a moment.'));
        setPending(false);
        return;
      }
      const stepRedirectUrl = mode === 'pre-event'
        ? ((data as Record<string, unknown>).redirectUrl as string) || replayRedirectUrl(trimmedEmail)
        : replayRedirectUrl(trimmedEmail);
      const needsAny = !!(data.needsFirstName || data.needsLastName);
      if (needsAny) {
        setStep2Data({
          token: (data as Record<string, unknown>).resumeToken as string,
          redirectUrl: stepRedirectUrl,
          knownFirstName: (data as Record<string, unknown>).knownFirstName as string | null ?? null,
          needsFirstName: !!(data as Record<string, unknown>).needsFirstName,
          needsLastName: !!(data as Record<string, unknown>).needsLastName,
        });
        setPending(false);
        setStep('step2');
      } else {
        navigateTop(stepRedirectUrl);
      }
    } catch (err) {
      console.error('late-register step1 failed', err);
      setError('Network error. Try again in a moment.');
      setPending(false);
    }
  }

  async function handleStep2(e: React.FormEvent) {
    e.preventDefault();
    if (!step2Data) return;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (step2Data.needsFirstName && !trimmedFirst) {
      setError('Please enter your first name.');
      return;
    }
    if (step2Data.needsLastName && !trimmedLast) {
      setError('Please enter your last name.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/embed/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: step2Data.token,
          ...(trimmedFirst ? { firstName: trimmedFirst } : {}),
          ...(trimmedLast ? { lastName: trimmedLast } : {}),
        }),
      });
      const data = await res.json().catch(() => ({})) as Record<string, unknown>;
      if (!res.ok) {
        setError((data as { error?: string }).error ?? 'Something went wrong. Try again in a moment.');
        setPending(false);
        return;
      }
      // 'pre-event': the server's OWN resumeUrl is correct here (lands on the
      // gate phase / GatePage confirmation) — prefer the fresh one this call
      // just returned over the step1-time value.
      // 'replay': keep step2Data.redirectUrl (the custom /replays/:slug?email=…
      // URL) — the server's resumeUrl points at the take page, which bounces to
      // a bare gate in replay phase (see the redirect assignment in handleStep1).
      const finalRedirect = mode === 'pre-event'
        ? ((data as Record<string, unknown>).resumeUrl as string) || step2Data.redirectUrl
        : step2Data.redirectUrl;
      navigateTop(finalRedirect);
    } catch (err) {
      console.error('late-register step2 failed', err);
      setError('Network error. Try again in a moment.');
      setPending(false);
    }
  }

  const step2Heading = step2Data
    ? (step2Data.needsFirstName && step2Data.needsLastName
        ? "Almost there — what's your name?"
        : step2Data.needsFirstName
          ? "One more thing — what's your first name?"
          : `One more thing, ${step2Data.knownFirstName?.trim() || 'there'} — what's your last name?`)
    : '';

  return (
    <div
      className="tsb-evt-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="late-register-heading"
    >
      <div className="tsb-evt-modal">
        <button
          type="button"
          className="tsb-evt-modal-close"
          onClick={onClose}
          aria-label="Close"
        >×</button>

        {step === 'step1' && (
          <>
            <h3 id="late-register-heading" className="tsb-evt-modal-heading">
              {heading ?? 'Two clicks. You get the replay + the next live date.'}
            </h3>
            <p className="tsb-evt-modal-body">
              {body ?? "We'll email the recording link right now, and the moment the next live cohort is locked in we'll send you a one-click link to save your seat."}
            </p>
            <form onSubmit={handleStep1} className="tsb-evt-modal-form">
              <label className="tsb-evt-modal-label">Email</label>
              <input
                type="email"
                autoComplete="email"
                autoFocus
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={pending}
                className="tsb-evt-modal-input"
              />
              <button
                type="submit"
                disabled={pending || !email.trim()}
                className="tsb-evt-modal-submit"
              >
                {pending ? 'Sending…' : (submitLabel ?? 'Send me the replay →')}
              </button>
              {/* ★ Mode-aware, for the same reason PR#1620 made the step-2
                  button mode-aware: this line was written for the replay
                  flow, and pre-event it promised "the replay link and a
                  heads-up when the next live is set" to someone registering
                  for a live event whose date is already on the page. */}
              <div className="tsb-evt-modal-fine">
                {mode === 'pre-event'
                  ? 'No credit card, no spam — just your seat and the details you need before the doors open.'
                  : 'No credit card, no spam — just the replay link and a heads-up when the next live is set.'}
              </div>
              {error && <div className="tsb-evt-modal-error">{error}</div>}
            </form>
          </>
        )}

        {step === 'step2' && step2Data && (
          <>
            <h3 id="late-register-heading" className="tsb-evt-modal-heading">
              {step2Heading}
            </h3>
            <form onSubmit={handleStep2} className="tsb-evt-modal-form">
              {step2Data.needsFirstName && (
                <>
                  <label className="tsb-evt-modal-label">First name</label>
                  <input
                    type="text"
                    autoComplete="given-name"
                    autoFocus
                    required
                    placeholder="Your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={pending}
                    className="tsb-evt-modal-input"
                  />
                </>
              )}
              {step2Data.needsLastName && (
                <>
                  <label className="tsb-evt-modal-label">Last name</label>
                  <input
                    type="text"
                    autoComplete="family-name"
                    autoFocus={!step2Data.needsFirstName}
                    required
                    placeholder="Your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={pending}
                    className="tsb-evt-modal-input"
                  />
                </>
              )}
              <button
                type="submit"
                disabled={
                  pending ||
                  (step2Data.needsFirstName && !firstName.trim()) ||
                  (step2Data.needsLastName && !lastName.trim())
                }
                className="tsb-evt-modal-submit"
              >
                {pending
                  ? 'One moment…'
                  : mode === 'pre-event'
                    ? (submitLabel ?? 'Get My Free Seat →')
                    : 'Get the replay →'}
              </button>
              {error && <div className="tsb-evt-modal-error">{error}</div>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ReplayWindowPillRender({ b }: { b: ReplayWindowPillBlock }) {
  return (
    <div className="tsb-evt-scope" style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
      <div className="tsb-evt-replay-pill tsb-evt-replay-pill-standalone" style={{ background: 'rgba(124,58,237,.18)' }}>
        <div className="tsb-evt-replay-pill-icon">{b.icon ?? '⏱'}</div>
        <div className="tsb-evt-replay-pill-text">
          <div className="tsb-evt-replay-pill-label" style={{ color: 'var(--tsb-purple)' }}>{b.label ?? 'Replay window'}</div>
          <div
            className="tsb-evt-replay-pill-time"
            style={{ color: 'var(--tsb-navy)' }}
            dangerouslySetInnerHTML={{ __html: formatTsbInline(b.text) }}
          />
        </div>
      </div>
    </div>
  );
}

function VideoFrameRender({ b }: { b: VideoFrameBlock }) {
  // Detect YouTube/Vimeo for embed conversion. Otherwise treat as direct video URL.
  function embedUrl(url: string): string {
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^?&]+)/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return url;
  }
  return (
    <section className="tsb-evt-scope tsb-evt-video-section">
      <div className="tsb-evt-container-wide">
        <div className="tsb-evt-video-frame">
          <div className="tsb-evt-video-aspect">
            {b.videoUrl ? (
              <iframe
                src={embedUrl(b.videoUrl)}
                title="Replay"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="tsb-evt-video-poster">
                <div className="tsb-evt-video-play">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </div>
                {b.posterLabel && (
                  <div
                    className="tsb-evt-video-poster-label"
                    dangerouslySetInnerHTML={{ __html: formatTsbInline(b.posterLabel) }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {b.bookmarks && b.bookmarks.length > 0 && (
          <div className="tsb-evt-bookmark-strip">
            {b.bookmarks.map((bm, i) => (
              <div key={i} className="tsb-evt-bookmark-chip">
                <span className="tsb-evt-bookmark-chip-time">{bm.time}</span> {bm.label}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HighlightReelRender({ b }: { b: HighlightReelBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section tint">
      <div className="tsb-evt-container">
        {(b.eyebrow || b.heading || b.intro) && (
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-section-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
            {b.intro && <p className="tsb-evt-section-intro">{b.intro}</p>}
          </div>
        )}
      </div>
      <div className="tsb-evt-highlight-list">
        {b.items.map((item, i) => (
          <div key={i} className="tsb-evt-highlight-item">
            <div className="tsb-evt-highlight-time">{item.time}</div>
            <div className="tsb-evt-highlight-body">
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ChatPullQuotesRender({ b }: { b: ChatPullQuotesBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-chatlog-section">
      <div className="tsb-evt-container">
        {(b.eyebrow || b.heading || b.intro) && (
          <div style={{ textAlign: 'center' }}>
            {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-section-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
            {b.intro && <p className="tsb-evt-section-intro">{b.intro}</p>}
          </div>
        )}
        <div className="tsb-evt-chatlog">
          {b.source && (
            <div className="tsb-evt-chatlog-head">
              <span className="tsb-evt-chatlog-head-dot" />
              {b.source}
            </div>
          )}
          {b.messages.map((m, i) => (
            <div key={i} className="tsb-evt-chatmsg">
              <div className={`tsb-evt-chatmsg-avatar${m.avatarVariant && m.avatarVariant !== 'coral' ? ` av-${m.avatarVariant}` : ''}`}>
                {m.initials}
              </div>
              <div className="tsb-evt-chatmsg-body">
                <div className="tsb-evt-chatmsg-meta">
                  <strong>{m.name}</strong>
                  {m.role && <span className="role">{m.role}</span>}
                  {m.timestamp && <span className="tsb-evt-chatmsg-time">{m.timestamp}</span>}
                </div>
                <div
                  className="tsb-evt-chatmsg-text"
                  dangerouslySetInnerHTML={{ __html: formatTsbInline(m.text) }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NextCohortCalloutRender({ b }: { b: NextCohortCalloutBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section tint">
      <div className="tsb-evt-cohort-callout-wrap">
        <div className="tsb-evt-cohort-callout">
          <div className="tsb-evt-cohort-callout-text">
            <h3>{b.heading}</h3>
            <p>{b.body}</p>
          </div>
          <a href={b.cta.url} className="tsb-evt-btn tsb-evt-btn-coral">{b.cta.label}</a>
        </div>
      </div>
    </section>
  );
}

function FinalCtaRender({ b }: { b: FinalCtaBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-final">
      <div className="tsb-evt-final-blobs" aria-hidden="true">
        <div /><div />
      </div>
      <div className="tsb-evt-final-inner" style={{ textAlign: 'center' }}>
        <h2 dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }} />
        {b.body && <p>{b.body}</p>}
        <a href={b.cta.url} className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-btn-lg">{b.cta.label}</a>
        {b.meta && <div className="tsb-evt-final-meta">{b.meta}</div>}
      </div>
    </section>
  );
}

function AttendeeStripRender({ b }: { b: AttendeeStripBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section light" style={{ paddingTop: 32, paddingBottom: 32 }}>
      <div className="tsb-evt-attendee-strip">
        <div className="tsb-evt-attendee-row">
          {b.avatars.map((a, i) => (
            <div key={i} className={`tsb-evt-attendee-avatar${a.variant && a.variant !== 'coral' ? ` av-${a.variant}` : ''}`}>
              {a.initials}
            </div>
          ))}
          {b.moreCount && (
            <div className="tsb-evt-attendee-avatar av-more">
              +{b.moreCount > 999 ? `${(b.moreCount/1000).toFixed(1).replace(/\.0$/, '')}k` : b.moreCount}
            </div>
          )}
        </div>
        <div
          className="tsb-evt-attendee-text"
          dangerouslySetInnerHTML={{ __html: formatTsbInline(b.text) }}
        />
      </div>
    </section>
  );
}

// =========================================================================
// Registration status card — confirmation + prep-survey layout for
// registration-type assessments.
// =========================================================================
function RegistrationStatusCardRender({ b }: { b: RegistrationStatusCardBlock }) {
  const counterRef = useRef<HTMLSpanElement | null>(null);
  const [counterValue, setCounterValue] = useState<number>(0);
  const [counterTarget, setCounterTarget] = useState<number | null>(null);
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    if (!b.counter) return;
    setCounterTarget(0); // live stats endpoint not called in steps variant for now
  }, [b.counter]);

  // Animated count-up when the counter enters the viewport.
  useEffect(() => {
    if (counterTarget === null || !counterRef.current) return;
    const target = counterTarget;
    const el = counterRef.current;
    let fired = false;
    const run = () => {
      if (fired) return;
      fired = true;
      const start = performance.now();
      const duration = 1200;
      const tick = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCounterValue(Math.round(eased * target));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if (typeof IntersectionObserver !== 'undefined') {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) run(); });
      }, { threshold: 0.3 });
      obs.observe(el);
      return () => obs.disconnect();
    }
    run();
  }, [counterTarget]);

  const host = b.host;

  const renderQuote = () => {
    if (!host?.quote) return null;
    const emphasis = host.quoteEmphasis;
    if (!emphasis || !host.quote.includes(emphasis)) {
      return <>&ldquo;{host.quote}&rdquo;</>;
    }
    const [before, after] = host.quote.split(emphasis);
    return <>&ldquo;{before}<strong>{emphasis}</strong>{after}&rdquo;</>;
  };

  return (
    <div className="block-regstatus">
      {b.eventTag && (
        <div className="block-regstatus-tag">
          <span className="block-regstatus-live-dot" aria-hidden="true" />
          <span>{b.eventTag}</span>
        </div>
      )}

      {b.heading && (
        <h2 className="block-regstatus-heading">{b.heading}</h2>
      )}

      {b.steps && b.steps.length > 0 && (
        <div className="block-regstatus-steps">
          {b.steps.map((s, i) => (
            <div key={i} className={`block-regstatus-step${s.done ? ' is-done' : ''}`}>
              <div className="block-regstatus-step-icon">{s.done ? '✓' : String(i + 1)}</div>
              <div className="block-regstatus-step-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {b.counter && (
        <div className="block-regstatus-counter">
          <div className="block-regstatus-counter-num">
            <span ref={counterRef}>{counterTarget === null ? '—' : counterValue}</span>
          </div>
          {b.counter.label && (
            <div className="block-regstatus-counter-text">
              <strong>{b.counter.label}</strong>
            </div>
          )}
          <div className="block-regstatus-counter-live" aria-label="Live">
            <span className="block-regstatus-live-dot" aria-hidden="true" />
            Live
          </div>
        </div>
      )}

      {host && (
        <div className="block-regstatus-host">
          <div className="block-regstatus-host-photo">
            {host.photoUrl && !photoFailed ? (
              <img
                src={host.photoUrl}
                alt={host.name ?? ''}
                onError={() => setPhotoFailed(true)}
              />
            ) : (
              <span className="block-regstatus-host-initials">
                {(host.name ?? host.slug ?? '?').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="block-regstatus-host-body">
            {(host.name || host.role) && (
              <div className="block-regstatus-host-name">
                {host.name}
                {host.role && <span className="block-regstatus-host-role"> · {host.role}</span>}
              </div>
            )}
            {host.quote && (
              <p className="block-regstatus-host-quote">{renderQuote()}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RegistrationLetterRender({ b }: { b: RegistrationStatusCardBlock }) {
  const [failedIndexes, setFailedIndexes] = useState<Set<number>>(new Set());
  const hostsToRender = (b.hosts && b.hosts.length > 0)
    ? b.hosts
    : (b.host ? [b.host] : []);

  // Lazy-load Caveat (handwriting font) only when this block renders.
  useEffect(() => {
    const id = 'caveat-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const renderPs = () => {
    if (!b.ps) return null;
    const emphasis = b.psEmphasis;
    if (!emphasis || !b.ps.includes(emphasis)) {
      return <><strong className="block-letter-ps-label">P.S.</strong>{' '}{b.ps}</>;
    }
    const [before, after] = b.ps.split(emphasis);
    return (
      <><strong className="block-letter-ps-label">P.S.</strong>{' '}{before}<strong>{emphasis}</strong>{after}</>
    );
  };

  return (
    <div className="block-letter">
      <div className="block-letter-layout">

        {/* ── Left: photo(s) + caption(s) — stacks vertically for multi-host ── */}
        <div className={`block-letter-photo-col${hostsToRender.length > 1 ? ' is-multi' : ''}`}>
          {hostsToRender.map((h, i) => {
            const failed = failedIndexes.has(i);
            const initial = (h.name ?? h.slug ?? '?').charAt(0).toUpperCase();
            return (
              <div key={i} className="block-letter-photo-item">
                <div className="block-letter-photo-wrap">
                  {h.photoUrl && !failed ? (
                    <img
                      src={h.photoUrl}
                      alt={h.name ?? ''}
                      onError={() => setFailedIndexes((prev) => { const next = new Set(prev); next.add(i); return next; })}
                    />
                  ) : (
                    <div className="block-letter-photo-initials">{initial}</div>
                  )}
                </div>
                {h.name && (
                  <div className="block-letter-photo-caption">
                    {h.name}
                    {h.role && <span> · {h.role}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Right: letter copy ── */}
        <div className="block-letter-right">
          {b.greeting && (
            <p className="block-letter-greeting">{b.greeting}</p>
          )}

          {b.paragraphs && b.paragraphs.length > 0 && (
            <div className="block-letter-body">
              {b.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          )}

          {(b.signature || b.signatureRole) && (
            <>
              <hr className="block-letter-divider" />
              <div className="block-letter-sig">
                {b.signature && <div className="block-letter-sig-name">{b.signature}</div>}
                {b.signatureRole && <div className="block-letter-sig-role">{b.signatureRole}</div>}
              </div>
            </>
          )}

          {b.ps && (
            <div className="block-letter-ps">{renderPs()}</div>
          )}

          {b.closesLabel && (
            <div className="block-letter-closes">{b.closesLabel}</div>
          )}
        </div>

      </div>
    </div>
  );
}

function HeadingRender({ b }: { b: HeadingBlock }) {
  const Tag = (`h${b.level ?? 2}`) as 'h1' | 'h2' | 'h3' | 'h4';
  return (
    <Tag
      className={`block-heading${b.accent ? ' accent' : ''}`}
      style={{ textAlign: b.align, color: b.color }}
    >
      {b.text}
    </Tag>
  );
}

function ParagraphRender({ b }: { b: ParagraphBlock }) {
  return (
    <p
      className={`block-paragraph size-${b.size ?? 'md'}`}
      style={{ textAlign: b.align, color: b.color }}
    >
      {b.text}
    </p>
  );
}

function ImageRender({ b }: { b: ImageBlock }) {
  return (
    <figure className={`block-image align-${b.align ?? 'center'}`}>
      <img
        src={b.url}
        alt={b.alt ?? ''}
        style={{
          width: b.width ?? '100%',
          borderRadius: b.rounded ? 'var(--radius)' : 0,
          boxShadow: b.shadow ? '0 12px 32px rgba(0,0,0,0.12)' : 'none',
        }}
      />
      {b.caption && <figcaption>{b.caption}</figcaption>}
    </figure>
  );
}

function CalloutRender({ b }: { b: CalloutBlock }) {
  // Support both 'text' and 'body' field names (config may use either)
  const text = b.text || (b as any).body || '';
  return (
    <div className={`block-callout variant-${b.variant ?? 'info'}`}>
      {b.icon && <div className="block-callout-icon">{b.icon}</div>}
      <div className="block-callout-content">
        {b.title && <div className="block-callout-title">{b.title}</div>}
        <div className="block-callout-text">{text}</div>
      </div>
    </div>
  );
}

function QuoteRender({ b }: { b: QuoteBlock }) {
  return (
    <blockquote className="block-quote">
      <p>&ldquo;{b.text}&rdquo;</p>
      {(b.attribution || b.avatarUrl) && (
        <footer className="block-quote-footer">
          {b.avatarUrl && <img src={b.avatarUrl} alt={b.attribution ?? ''} className="block-quote-avatar" />}
          <div>
            {b.attribution && <div className="block-quote-attribution">— {b.attribution}</div>}
            {b.role && <div className="block-quote-role">{b.role}</div>}
          </div>
        </footer>
      )}
    </blockquote>
  );
}

function StatRender({ b }: { b: StatBlock }) {
  return (
    <div className="block-stat">
      <div className="block-stat-value" style={{ color: b.color }}>
        {b.value}
        {b.trend && <span className={`block-stat-trend ${b.trend}`}>{b.trend === 'up' ? '▲' : b.trend === 'down' ? '▼' : '—'}</span>}
      </div>
      <div className="block-stat-label">{b.label}</div>
    </div>
  );
}

function StatsGridRender({ b }: { b: StatsGridBlock }) {
  // Support both 'items' and 'stats' field names (config may use either)
  const items = b.items || (b as any).stats || [];
  // 'navy' variant: full-bleed navy band with coral numbers + dim labels.
  // Used on event landings (e.g. 43,000+ / 90 min / Free).
  if (b.variant === 'navy') {
    return (
      <section className="tsb-evt-scope tsb-evt-stats-strip">
        {(b.eyebrow || b.heading) && (
          <div className="tsb-evt-stats-head">
            {b.eyebrow && <div className="tsb-evt-stats-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-stats-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
          </div>
        )}
        <div className="tsb-evt-stats-grid" style={{ gridTemplateColumns: `repeat(${b.columns ?? items.length}, minmax(0, 1fr))` }}>
          {items.map((it: any, i: number) => (
            <div key={i} className="tsb-evt-stat-item">
              <div className="tsb-evt-stat-num">{it.value}</div>
              <div className="tsb-evt-stat-desc">{it.label}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  // Default: existing light card grid behaviour.
  return (
    <div className="block-stats-grid" style={{ gridTemplateColumns: `repeat(${b.columns ?? items.length}, minmax(0, 1fr))` }}>
      {items.map((it: any, i: number) => (
        <div key={i} className="block-stat">
          <div className="block-stat-value" style={{ color: it.color }}>{it.value}</div>
          <div className="block-stat-label">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function ListRender({ b }: { b: ListBlock }) {
  const style = b.style ?? 'bullet';
  const variantClass = b.variant && b.variant !== 'stack' ? ` variant-${b.variant}` : '';
  if (style === 'numbered') {
    return <ol className={`block-list numbered${variantClass}`}>{b.items.map((it, i) => <li key={i}>{it}</li>)}</ol>;
  }
  return (
    <ul className={`block-list ${style}${variantClass}`}>
      {b.items.map((it, i) => (
        <li key={i}>
          {style === 'check' && <span className="block-list-check">✓</span>}
          {it}
        </li>
      ))}
    </ul>
  );
}

function DiagramRender({ b }: { b: DiagramBlock }) {
  return (
    <div className="block-diagram" style={{ background: b.background }}>
      <div dangerouslySetInnerHTML={{ __html: b.svg }} />
      {b.caption && <div className="block-diagram-caption">{b.caption}</div>}
    </div>
  );
}

function VideoRender({ b }: { b: VideoBlock }) {
  return (
    <div className="block-video">
      <video
        src={b.url}
        poster={b.poster}
        autoPlay={b.autoplay}
        loop={b.loop}
        controls
        playsInline
      />
      {b.caption && <div className="block-video-caption">{b.caption}</div>}
    </div>
  );
}

function CtaRender({ b }: { b: CtaBlock }) {
  return (
    <div className={`block-cta align-${b.align ?? 'center'}`}>
      <a href={b.url} className={`btn btn-${b.variant ?? 'primary'}`} target="_blank" rel="noreferrer">
        {b.label}
      </a>
    </div>
  );
}

function IconRender({ b }: { b: IconBlock }) {
  const isUrl = /^https?:\/\//.test(b.icon);
  return (
    <div className={`block-icon align-${b.align ?? 'center'}`} style={{ fontSize: b.size ?? '56px' }}>
      {isUrl ? <img src={b.icon} alt="" style={{ height: b.size ?? '56px' }} /> : b.icon}
    </div>
  );
}

function SpacerRender({ b }: { b: SpacerBlock }) {
  const map = { xs: 8, sm: 16, md: 24, lg: 40, xl: 64 };
  return <div style={{ height: map[b.size ?? 'md'] }} />;
}

function ColumnsRender({ b }: { b: ColumnsBlock }) {
  const gapMap = { sm: '12px', md: '24px', lg: '40px' };
  const ratio = b.ratio ? b.ratio.split(':').map((n) => `${n}fr`).join(' ') : `repeat(${b.columns.length}, 1fr)`;
  // Normalise: each col may be ContentBlock[] (canonical) or { blocks: ContentBlock[] } (wrapper shape).
  const cols = b.columns.map((col: any) => Array.isArray(col) ? col : (col?.blocks ?? []));
  return (
    <div
      className="block-columns"
      style={{
        gridTemplateColumns: ratio,
        gap: gapMap[b.gap ?? 'md'],
        alignItems: b.alignItems ?? 'start',
      }}
    >
      {cols.map((col, i) => (
        <div key={i}>{col.map((inner: ContentBlock, j: number) => <Block key={j} block={inner} />)}</div>
      ))}
    </div>
  );
}

function CardRender({ b }: { b: CardBlock }) {
  const padMap = { sm: '16px', md: '24px', lg: '36px' };
  // Support both shapes:
  //   A. { blocks: ContentBlock[] } — nested block array (original)
  //   B. { title?: string, items?: (string | ContentBlock)[] } — string-list shape
  //      that CAI / admins naturally produce. Missing both → empty card (no crash).
  const anyB = b as any;
  const nestedBlocks: ContentBlock[] | undefined = Array.isArray(anyB.blocks) ? anyB.blocks : undefined;
  const title: string | undefined = typeof anyB.title === 'string' ? anyB.title : undefined;
  const items: Array<string | ContentBlock> | undefined = Array.isArray(anyB.items) ? anyB.items : undefined;

  return (
    <div
      className={`block-card${b.bordered ? ' bordered' : ''}`}
      style={{ background: b.background, padding: padMap[b.padding ?? 'md'] }}
    >
      {title && <div className="block-card-title">{title}</div>}
      {nestedBlocks && nestedBlocks.map((inner, i) => <Block key={`b${i}`} block={inner} />)}
      {items && items.length > 0 && (
        <ul className="block-card-items">
          {items.map((it, i) => (
            <li key={`i${i}`}>
              {typeof it === 'string' ? it : <Block block={it} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HtmlRender({ b }: { b: HtmlBlock }) {
  return <div className="block-html" dangerouslySetInnerHTML={{ __html: b.html }} />;
}

// =========================================================================
// Landing-page block renderers
// =========================================================================

function HeroRender({ b }: { b: HeroBlock }) {
  // Resolve background: new `background` field takes priority over legacy `backgroundUrl`.
  const bg = b.background ?? (b.backgroundUrl ? { type: 'image' as const, url: b.backgroundUrl } : null);
  const imageStyle = bg?.type === 'image' ? { backgroundImage: `url(${bg.url})` } : undefined;
  const needsRelativeShell = bg && bg.type !== 'image';

  return (
    <div
      className="block-hero"
      style={{
        ...imageStyle,
        ...(needsRelativeShell ? { position: 'relative', overflow: 'hidden' } : undefined),
      }}
    >
      {bg && bg.type !== 'image' && <HeroBackgroundLayer bg={bg} />}
      <span className="block-hero-content" style={{ position: 'relative', zIndex: 2 }}>
        {b.badge && <span className="block-hero-badge">{b.badge}</span>}
        {b.h1Html
          ? <h1 className="block-hero-h1" dangerouslySetInnerHTML={{ __html: b.h1Html }} />
          : <h1 className="block-hero-h1">{b.h1}</h1>
        }
        {b.subhead && <p className="block-hero-subhead">{b.subhead}</p>}
        {b.ctaLabel && b.ctaUrl && (
          <a href={b.ctaUrl} className="btn btn-primary block-hero-cta">{b.ctaLabel}</a>
        )}
      </span>
    </div>
  );
}

function MoneyMathRender({ b }: { b: MoneyMathBlock }) {
  // Three-state variant: Where you are → What's possible → Here's the gap
  if (b.variant === 'threeState' && b.whereYouAre && b.whatsPossible && b.gap) {
    const renderState = (s: { headline: string; math?: string; caption?: string }, eyebrow: string, extraClass = '') => (
      <div className={`block-money-math-state${extraClass ? ' ' + extraClass : ''}`}>
        <div className="block-money-math-state-eyebrow">{eyebrow}</div>
        <div className="block-money-math-state-number">{s.headline}</div>
        {s.math && <div className="block-money-math-state-math">{s.math}</div>}
        {s.caption && <div className="block-money-math-state-caption">{s.caption}</div>}
      </div>
    );
    return (
      <div className="block-money-math threestate">
        {b.label && <div className="block-money-math-label">{b.label}</div>}
        <div className="block-money-math-threestate-grid">
          {renderState(b.whereYouAre, 'Where you are')}
          <div className="block-money-math-arrow" aria-hidden="true">→</div>
          {renderState(b.whatsPossible, "What's possible")}
          <div className="block-money-math-arrow" aria-hidden="true">→</div>
          {renderState(b.gap, "Here's the gap", 'block-money-math-state-gap')}
        </div>
        {b.body && <p className="block-money-math-body">{b.body}</p>}
      </div>
    );
  }

  // Legacy row variant
  return (
    <div className="block-money-math">
      {b.label && <div className="block-money-math-label">{b.label}</div>}
      <div className="block-money-math-row">
        {(b.values ?? []).map((v, i) => (
          v.symbol
            ? <div key={i} className="block-money-math-symbol">{v.symbol}</div>
            : <div key={i} className="block-money-math-cell">
                <div className="block-money-math-number">{v.number}</div>
                {v.desc && <div className="block-money-math-desc">{v.desc}</div>}
              </div>
        ))}
      </div>
      {b.body && <p className="block-money-math-body">{b.body}</p>}
    </div>
  );
}

function TickerRender({ b }: { b: TickerBlock }) {
  // Duplicate items for infinite scroll effect
  const doubled = [...b.items, ...b.items];
  return (
    <div className="block-ticker">
      <div className="block-ticker-track">
        {doubled.map((item, i) => (
          <span key={i} className="block-ticker-item">{item}</span>
        ))}
      </div>
    </div>
  );
}

function PillarCardsRender({ b }: { b: PillarCardsBlock }) {
  return (
    <div className="block-pillar-cards">
      {b.pillars.map((p, i) => (
        <div key={i} className={`block-pillar-card${p.fullWidth ? ' full-width' : ''}`}>
          <div className="block-pillar-number">{p.number}</div>
          <div className="block-pillar-name">{p.name}</div>
          {p.question && <div className="block-pillar-question">{p.question}</div>}
          {p.problem && <div className="block-pillar-problem">{p.problem}</div>}
          {p.outcome && (
            <div className="block-pillar-outcome">
              {p.outcomeLabel && <div className="block-pillar-outcome-label">{p.outcomeLabel}</div>}
              <div>{p.outcome}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Initials fallback for a testimonial with no portrait. Two letters max:
 *  first + last initial, or the first two letters of a single-word name. */
function testimonialInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '\u2014';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ProofQuotesRender({ b }: { b: ProofQuotesBlock }) {
  // 'portrait-slider' variant: horizontal scroll-snap rail of portrait cards.
  // Breadth is the argument — the reader is meant to feel there are more people
  // than they can read in one pass, so the rail deliberately shows a partial
  // next card and fades its right edge rather than paginating.
  //
  // Deliberately CSS-only: no carousel library, no autoplay, no JS scroll
  // handlers. Native overflow scrolling already gives us touch/trackpad
  // momentum, keyboard arrows (via tabIndex on the rail) and reduced-motion
  // behaviour for free — every one of which a hand-rolled carousel would have
  // to reimplement and would get wrong on some device.
  if (b.variant === 'carousel') {
    return <ProofCarousel b={b} />;
  }

  if (b.variant === 'portrait-slider') {
    const items = b.quotes ?? [];
    if (items.length === 0) return null;
    // Strip inline-format tokens ({{em:...}}) so the a11y label is plain text.
    const railLabel = (b.heading || '').replace(/\{\{\w+:([^}]*)\}\}/g, '$1').trim() || 'Testimonials';
    return (
      <section className="tsb-evt-scope tsb-evt-section light">
        <div className="tsb-evt-container">
          {(b.eyebrow || b.heading || b.lede) && (
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
              {b.heading && (
                <h2
                  className="tsb-evt-section-heading"
                  dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
                />
              )}
              {b.lede && <p className="tsb-evt-tslider-lede">{b.lede}</p>}
            </div>
          )}
          {b.scrollHint && (
            <div className="tsb-evt-tslider-hint" aria-hidden="true">{b.scrollHint}</div>
          )}
          <div className="tsb-evt-tslider-wrap">
            <ul
              className="tsb-evt-tslider"
              role="region"
              aria-label={railLabel}
              tabIndex={0}
            >
              {items.map((q, i) => (
                <li key={i} className="tsb-evt-tcard">
                  <div className="tsb-evt-tcard-media">
                    {q.avatarUrl ? (
                      <img src={q.avatarUrl} alt={q.name ?? ''} loading="lazy" decoding="async" />
                    ) : (
                      // No portrait supplied — render a monogram rather than
                      // dropping the person. A silently missing testimonial is
                      // worse than an imperfect one.
                      <span className="tsb-evt-tcard-monogram" aria-hidden="true">
                        {testimonialInitials(q.name)}
                      </span>
                    )}
                  </div>
                  <div className="tsb-evt-tcard-body">
                    {q.badge && <span className="tsb-evt-tcard-result">{q.badge}</span>}
                    <blockquote>&ldquo;{q.quote}&rdquo;</blockquote>
                    <div className="tsb-evt-tcard-attr">
                      <span className="tsb-evt-tcard-name">{q.name}</span>
                      {q.company && <span className="tsb-evt-tcard-firm">{q.company}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }
  // 'cards-with-result' variant: 2-column white card grid with green RESULT
  // pills for badges + optional coral-tinted bottom stats panel. Designed for
  // event-landing proof sections.
  if (b.variant === 'cards-with-result') {
    return (
      <section className="tsb-evt-scope tsb-evt-section light">
        <div className="tsb-evt-container">
          {(b.eyebrow || b.heading) && (
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
              {b.heading && (
                <h2
                  className="tsb-evt-section-heading"
                  dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
                />
              )}
            </div>
          )}
          <div className="tsb-evt-proof-grid">
            {b.quotes.map((q, i) => (
              <div key={i} className="tsb-evt-proof-card">
                {q.badge && <span className="tsb-evt-proof-result">{q.badge}</span>}
                <blockquote>&ldquo;{q.quote}&rdquo;</blockquote>
                <div className="tsb-evt-proof-name">
                  {q.name}{q.company ? ` · ${q.company}` : ''}
                </div>
              </div>
            ))}
          </div>
          {b.bottomStats && b.bottomStats.length > 0 && (
            <div className="tsb-evt-proof-stats">
              {b.bottomStats.map((s, i) => (
                <div key={i} className="tsb-evt-ps-item">
                  <div className="tsb-evt-ps-num">{s.value}</div>
                  <div className="tsb-evt-ps-lbl">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }
  // Default: existing quote-stack layout.
  return (
    <div className="block-proof-quotes">
      {b.quotes.map((q, i) => (
        <div key={i} className="block-proof-quote">
          {q.badge && <span className="block-proof-badge">{q.badge}</span>}
          <blockquote>&ldquo;{q.quote}&rdquo;</blockquote>
          <div className="block-proof-footer">
            {q.avatarUrl && <img src={q.avatarUrl} alt={q.name ?? ''} className="block-proof-avatar" />}
            <div>
              <div className="block-proof-name">{q.name}</div>
              {q.company && <div className="block-proof-company">{q.company}</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// Countdown — animated days/hours/minutes/seconds counter to a target ISO
// datetime. Renders an expired-state copy after the target has passed.
// =========================================================================
/** Shared countdown math for both the standalone `countdown` block and the
 *  eventHero split-layout countdown card. Extracted so a fix to one can never
 *  leave the other on stale logic. Returns null for an unparseable target so
 *  callers render nothing rather than NaN tiles. */
function useCountdown(targetIso: string, hideSeconds?: boolean) {
  const [now, setNow] = useState<number>(() => Date.now());
  const target = useRef<number>(NaN);
  if (Number.isNaN(target.current)) target.current = new Date(targetIso).getTime();

  useEffect(() => {
    if (Number.isNaN(target.current)) return;
    const tick = hideSeconds ? 30000 : 1000;
    const id = window.setInterval(() => setNow(Date.now()), tick);
    return () => window.clearInterval(id);
  }, [hideSeconds]);

  if (Number.isNaN(target.current)) return null;

  const diff = Math.max(0, target.current - now);
  return {
    expired: diff === 0,
    days:    Math.floor(diff / 86_400_000),
    hours:   Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1000),
  };
}

const pad2 = (n: number) => String(n).padStart(2, '0');

function CountdownRender({ b }: { b: CountdownBlock }) {
  const t = useCountdown(b.target, b.hideSeconds);
  if (!t) {
    // Bad target — render nothing rather than NaN tiles.
    return null;
  }
  const { days, hours, minutes, seconds, expired } = t;
  const pad = pad2;

  if (expired) {
    return (
      <div className="block-countdown is-expired">
        {b.expiredHeadline && <div className="block-countdown-expired-headline">{b.expiredHeadline}</div>}
        {b.expiredBody && <p className="block-countdown-expired-body">{b.expiredBody}</p>}
      </div>
    );
  }

  return (
    <div className="block-countdown">
      {b.label && <div className="block-countdown-label">{b.label}</div>}
      <div className="block-countdown-tiles" role="timer" aria-live="off">
        <Tile value={String(days)} unit="days" />
        <Tile value={pad(hours)} unit="hrs" />
        <Tile value={pad(minutes)} unit="min" />
        {!b.hideSeconds && <Tile value={pad(seconds)} unit="sec" />}
      </div>
      {b.caption && <div className="block-countdown-caption">{b.caption}</div>}
    </div>
  );
}

function Tile({ value, unit }: { value: string; unit: string }) {
  return (
    <div className="block-countdown-tile">
      <div className="block-countdown-tile-value">{value}</div>
      <div className="block-countdown-tile-unit">{unit}</div>
    </div>
  );
}

// =========================================================================
// FAQ — accordion of question/answer pairs using native <details>.
// =========================================================================
function FaqRender({ b }: { b: FaqBlock }) {
  return (
    <div className="block-faq">
      {b.heading && <h2 className="block-faq-heading">{b.heading}</h2>}
      <div className="block-faq-list">
        {b.items.map((item, i) => (
          <details
            key={i}
            className="block-faq-item"
            open={i === 0 && b.defaultFirstOpen ? true : undefined}
          >
            <summary className="block-faq-q">
              <span className="block-faq-q-text">{item.question}</span>
              <span className="block-faq-q-icon" aria-hidden="true">+</span>
            </summary>
            <div className="block-faq-a">
              {item.answerHtml
                ? <div dangerouslySetInnerHTML={{ __html: item.answerHtml }} />
                : <p>{item.answer}</p>}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

// =========================================================================
// Host bio — standalone host card (one or more hosts).
// =========================================================================
function HostBioRender({ b }: { b: HostBioBlock }) {
  const [failedIndexes, setFailedIndexes] = useState<Set<number>>(new Set());
  const hosts = b.hosts ?? [];
  if (hosts.length === 0) return null;

  const isMulti = hosts.length > 1;
  const layoutClass = isMulti ? 'is-multi' : `layout-${b.layout ?? 'row'}`;

  return (
    <div className={`block-host-bio ${layoutClass}`}>
      {b.eyebrow && <div className="block-host-bio-eyebrow">{b.eyebrow}</div>}
      {b.heading && <h2 className="block-host-bio-heading">{b.heading}</h2>}
      <div className="block-host-bio-list">
        {hosts.map((h, i) => {
          const failed = failedIndexes.has(i);
          const initial = (h.name ?? h.slug ?? '?').charAt(0).toUpperCase();
          return (
            <div key={i} className="block-host-bio-card">
              <div className="block-host-bio-photo">
                {h.photoUrl && !failed ? (
                  <img
                    src={h.photoUrl}
                    alt={h.name ?? ''}
                    onError={() => setFailedIndexes((prev) => { const next = new Set(prev); next.add(i); return next; })}
                  />
                ) : (
                  <span className="block-host-bio-initial">{initial}</span>
                )}
              </div>
              <div className="block-host-bio-body">
                {h.name && <div className="block-host-bio-name">{h.name}</div>}
                {h.subtitle && <div className="block-host-bio-subtitle">{h.subtitle}</div>}
                {h.role && <div className="block-host-bio-role">{h.role}</div>}
                {h.bio && <p className="block-host-bio-text">{h.bio}</p>}
                {h.bodyParagraphs && h.bodyParagraphs.length > 0 && (
                  <div className="block-host-bio-extra">
                    {h.bodyParagraphs.map((p, pi) => (
                      <p key={pi} className="block-host-bio-text">{p}</p>
                    ))}
                  </div>
                )}
                {h.quote && (
                  <div className="block-host-bio-quote">
                    {h.quoteEmphasis && h.quote.includes(h.quoteEmphasis) ? (
                      (() => {
                        const [before, after] = h.quote.split(h.quoteEmphasis);
                        return <>&ldquo;{before}<strong>{h.quoteEmphasis}</strong>{after}&rdquo;</>;
                      })()
                    ) : (
                      <>&ldquo;{h.quote}&rdquo;</>
                    )}
                  </div>
                )}
                {h.creds && h.creds.length > 0 && (
                  <ul className="block-host-bio-creds">
                    {h.creds.map((c, ci) => (
                      <li key={ci}>{c}</li>
                    ))}
                  </ul>
                )}
                {(h.linkedinUrl || h.instagramUrl) && (
                  <div className="block-host-bio-links">
                    {h.linkedinUrl && (
                      <a href={h.linkedinUrl} target="_blank" rel="noreferrer">LinkedIn</a>
                    )}
                    {h.instagramUrl && (
                      <a href={h.instagramUrl} target="_blank" rel="noreferrer">Instagram</a>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =========================================================================
// Replay video — embedded YouTube/Vimeo/Wistia or direct mp4, plus CTA.
// =========================================================================
function ReplayVideoRender({ b }: { b: ReplayVideoBlock }) {
  const embedUrl = toEmbedUrl(b.videoUrl);
  const isIframe = embedUrl !== null;
  // A videoUrl that is neither an embeddable provider URL (YouTube/Vimeo/Wistia)
  // nor a direct media file is a LINK to the gated replay page (/r/replay/:slug →
  // /replays/:slug). The take page is marketing — it must NOT play the recording
  // inline. Render a poster thumbnail with a play overlay that sends the visitor
  // to the gated page to enter their email and watch. (Incident 2026-07-13 — the
  // Jul-8 CBL exposed a raw <video> here that both broke and, if it had worked,
  // would have ungated the replay.) Unresolved {{tokens}} fall through to <video>.
  const isMediaFile = /\.(mp4|webm|ogg|mov|m3u8)(\?|$)/i.test(b.videoUrl);
  const isGatedLink =
    !isIframe && !isMediaFile && !b.videoUrl.includes('{{') && /^(https?:\/\/|\/)/.test(b.videoUrl);

  return (
    // id="replay-video" is the scroll target for the siteNav + eventHero
    // "Watch the replay" CTAs, whose href is #replay-video. Without it those
    // buttons are dead (they resolve to no element and do nothing). scrollMarginTop
    // clears the ~72px sticky nav so the poster isn't hidden under it on jump.
    <div className="block-replay-video" id="replay-video" style={{ scrollMarginTop: 88 }}>
      {b.heading && <h2 className="block-replay-video-heading">{b.heading}</h2>}
      {b.body && <p className="block-replay-video-body">{b.body}</p>}
      <div className="block-replay-video-frame">
        {isIframe ? (
          <iframe
            src={embedUrl!}
            title={b.heading ?? 'Replay video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : isGatedLink ? (
          <a
            href={b.videoUrl}
            className="block-replay-video-poster"
            aria-label={b.heading ?? 'Watch the replay'}
            style={{
              display: 'block',
              background: b.posterUrl ? `#000 center/cover no-repeat url(${JSON.stringify(b.posterUrl)})` : '#1A1A2E',
              textDecoration: 'none',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 76, height: 76, borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)', border: '2px solid rgba(255,255,255,0.9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <span style={{
                width: 0, height: 0, marginLeft: 6,
                borderTop: '14px solid transparent', borderBottom: '14px solid transparent',
                borderLeft: '22px solid #fff',
              }} />
            </span>
          </a>
        ) : (
          <video src={b.videoUrl} poster={b.posterUrl} controls playsInline />
        )}
      </div>
      {b.ctaLabel && b.ctaUrl && (
        <div className="block-replay-video-cta">
          <a href={b.ctaUrl} className="btn btn-primary" target="_blank" rel="noreferrer">
            {b.ctaLabel}
          </a>
        </div>
      )}
    </div>
  );
}

/** Convert YouTube / Vimeo / Wistia URLs to their embed form. Returns null
 *  for unrecognized URLs (caller falls back to native <video src>). */
function toEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');

    // YouTube — youtu.be/<id> or youtube.com/watch?v=<id> or youtube.com/embed/<id>
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname.startsWith('/embed/')) return url;
      const id = u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    // Vimeo — vimeo.com/<id>
    if (host === 'vimeo.com') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
    }
    if (host === 'player.vimeo.com') return url;

    // Wistia — <domain>.wistia.com/medias/<id> or fast.wistia.net/embed/iframe/<id>
    if (host.endsWith('.wistia.com')) {
      const m = u.pathname.match(/\/medias\/([A-Za-z0-9]+)/);
      return m ? `https://fast.wistia.net/embed/iframe/${m[1]}` : null;
    }
    if (host === 'fast.wistia.net') return url;

    return null;
  } catch {
    return null;
  }
}


// =========================================================================
// PHASE 2 PATH A — additional TSB event-landing primitives.
// =========================================================================

function AnnounceBarRender({ b }: { b: AnnounceBarBlock }) {
  const variant = b.variant ?? 'navy';
  const gate = b.geoGate;
  // No gate → open immediately (existing unconditional-bar behavior). A
  // gated bar starts closed and only opens once geo resolves to a match —
  // fails closed, so an unresolved/unknown country never shows it.
  const [gateOpen, setGateOpen] = useState(!gate);

  useEffect(() => {
    if (!gate) return;
    let cancelled = false;
    fetchGeoCountry().then((country) => {
      if (cancelled || !country) return;
      const inList = gate.countries.map((c) => c.toUpperCase()).includes(country.toUpperCase());
      const matches = gate.mode === 'include' ? inList : !inList;
      if (matches) setGateOpen(true);
    });
    return () => { cancelled = true; };
    // Mount-only — one geo pass per page; block config is static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windowed = b.showFrom !== undefined || b.showUntil !== undefined;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!windowed) return;
    const t = setInterval(() => setNowMs(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [windowed]);

  if (!gateOpen) return null;
  if (windowed && !announceWindowOpen(b, nowMs)) return null;

  return (
    <div className={`tsb-evt-scope tsb-evt-announce-bar variant-${variant}${b.emphasis ? ` is-${b.emphasis}` : ''}`}>
      {b.showDot && <span className="tsb-evt-announce-dot" />}
      <span dangerouslySetInnerHTML={{ __html: formatTsbInline(b.text) }} />
      {b.cta && (
        <a href={b.cta.url} className="tsb-evt-announce-cta">
          {b.cta.label}
        </a>
      )}
    </div>
  );
}

function StepsListRender({ b }: { b: StepsListBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section light">
      <div className="tsb-evt-container">
        {(b.eyebrow || b.heading || b.intro) && (
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-section-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
            {b.intro && <p className="tsb-evt-section-intro">{b.intro}</p>}
          </div>
        )}
        <div className="tsb-evt-steps-list">
          {b.items.map((item, i) => (
            <div key={i} className="tsb-evt-step-card">
              <div className="tsb-evt-step-num">{item.number}</div>
              <div className="tsb-evt-step-body">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PerksListRender({ b }: { b: PerksListBlock }) {
  return (
    <section className="tsb-evt-scope tsb-evt-section light">
      <div className="tsb-evt-container">
        {(b.eyebrow || b.heading || b.intro) && (
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-section-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
            {b.intro && <p className="tsb-evt-section-intro">{b.intro}</p>}
          </div>
        )}
        <div className="tsb-evt-perks-list">
          {b.items.map((item, i) => (
            <div key={i} className="tsb-evt-perk-item">
              <div className="tsb-evt-perk-icon">{item.icon}</div>
              <div className="tsb-evt-perk-body">
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =========================================================================
// PricingTiersRender — geo-resolved ticket-tier pricing for paid events
// (Summit). Contract: docs/summit-migration/PHASE2-CONTRACTS.md §5.
//
// Behavior:
//   • First paint is ALWAYS in block.baseCurrency — no waiting on network.
//   • The currency comes from resolveEventCurrency (lib/geoCurrency) — the
//     SAME rule CheckoutPage uses, so card and charge always agree. The event
//     sells only in the currencies present in its tiers' pricing maps; a
//     visitor sees their geo currency (CA→CAD, AU/NZ→AUD, US→USD, else USD)
//     only when the event sells in it, otherwise the block's baseCurrency.
//     The AU Summit sells in AUD alone, so EVERY visitor there — PH, US,
//     anywhere — sees AUD (mct_a9303307-0e5, 2026-09-30; this replaced the
//     old "non-AU/NZ visitor on the AU page sees USD" rule). A swap re-renders
//     only the price TEXT (no layout shift).
//   • ★ NO manual switcher — Michael's call, 2026-09-16: geo decides, the
//     visitor never picks. Do not re-add a currency toggle to this block.
//   • Paid CTAs carry the resolved currency as `?cur=` so /checkout/<slug>
//     opens in the same currency. Anchor CTAs (#register) are untouched.
//   • Free tiers (no pricing map) show `freeLabel` (default "FREE").
//   • memberCallout renders under the cards as a distinct navy band with the
//     "Already a member?" note line. `memberCalloutPosition: 'top'` lifts it
//     ABOVE the cards instead, for a page that leads with membership (Summit
//     2027 AU, mca_d43f6357c0ad). Omitted = under the cards, unchanged.
// =========================================================================

/** Append the chosen currency to a paid CTA href. Hash anchors and empty
 *  URLs pass through untouched. */
function withCurrencyParam(url: string, currency: string): string {
  if (!url || url.startsWith('#')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}cur=${encodeURIComponent(currency)}`;
}

/** `$45` for whole dollars, `$64.50` when the price carries cents.
 *  All contract currencies use $. */
function fmtDollars(amount: number): string {
  const cents = !Number.isInteger(amount);
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Every currency code present across the tiers' pricing maps, baseCurrency
 *  first, then in order of first appearance. */
function currenciesInTiers(tiers: PricingTierCard[], baseCurrency: string): string[] {
  const out: string[] = [];
  const push = (c: string) => { if (c && !out.includes(c)) out.push(c); };
  push(baseCurrency);
  for (const t of tiers) {
    for (const code of Object.keys(t.pricing ?? {})) push(code);
  }
  return out;
}

function PricingTiersRender({ b }: { b: PricingTiersBlock }) {
  const currencies = currenciesInTiers(b.tiers ?? [], b.baseCurrency);
  const [currency, setCurrency] = useState(b.baseCurrency);

  useEffect(() => {
    let cancelled = false;
    fetchGeoCountry().then((country) => {
      if (cancelled) return;
      // Same rule as CheckoutPage — the event's own currencies are
      // `currencies` (every code in the tiers' pricing maps).
      const cur = resolveEventCurrency({
        country,
        offered: currencies,
        base: b.baseCurrency,
        map: b.currencyByCountry ?? null,
      });
      if (cur && cur !== b.baseCurrency) setCurrency(cur);
    });
    return () => { cancelled = true; };
    // Mount-only by design: one geo pass per page; block config is static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderPrice(t: PricingTierCard) {
    if (!t.pricing || Object.keys(t.pricing).length === 0) {
      return <div className="tsb-evt-pricing-free">{t.freeLabel ?? 'FREE'}</div>;
    }
    // Prefer the chosen currency; fall back to the base currency (and label
    // the price with the code actually shown so it never lies).
    const entry = t.pricing[currency] ?? t.pricing[b.baseCurrency] ?? Object.values(t.pricing)[0];
    const shownCode = t.pricing[currency] ? currency
      : t.pricing[b.baseCurrency] ? b.baseCurrency
      : Object.keys(t.pricing)[0];
    if (!entry) return null;
    const label = `${fmtDollars(entry.amount)} ${shownCode}${entry.wasAmount != null ? `, was ${fmtDollars(entry.wasAmount)}` : ''}`;
    return (
      <div className="tsb-evt-pricing-price" aria-label={label}>
        {entry.wasAmount != null && (
          <s className="tsb-evt-pricing-was" aria-hidden="true">{fmtDollars(entry.wasAmount)}</s>
        )}
        <span className="tsb-evt-pricing-amount" aria-hidden="true">{fmtDollars(entry.amount)}</span>
        <span className="tsb-evt-pricing-code" aria-hidden="true">{shownCode}</span>
      </div>
    );
  }

  /** "+$19.50 gets the Lisa Campbell workshop and 100+ sessions" — computed in
   *  the CURRENT currency from the two tiers' own pricing maps, never a
   *  hand-typed dollar figure that could drift when a price changes or a
   *  visitor's currency differs (VIP's delta over All Access is $15 USD but
   *  $19.50 CAD/AUD — a single hardcoded string can't be right in both). */
  function renderDeltaNote(t: PricingTierCard) {
    if (!t.deltaNote || !t.pricing) return null;
    const other = (b.tiers ?? []).find((x) => x.key === t.deltaNote!.fromTierKey);
    const mine = t.pricing[currency] ?? t.pricing[b.baseCurrency];
    const theirs = other?.pricing?.[currency] ?? other?.pricing?.[b.baseCurrency];
    if (!mine || !theirs) return null; // either side free/missing — say nothing rather than guess
    const delta = mine.amount - theirs.amount;
    if (delta <= 0) return null; // never show a non-positive "upgrade" delta
    return (
      <div className="tsb-evt-pricing-delta">
        {t.deltaNote.template.replace('{delta}', fmtDollars(delta))}
      </div>
    );
  }

  const mc = b.memberCallout;
  const mcTop = b.memberCalloutPosition === 'top';
  const memberBand = mc && (
    <div className="tsb-evt-container">
      <div className={`tsb-evt-pricing-member${mcTop ? ' is-top' : ''}`}>
        <div className="tsb-evt-pricing-member-text">
          <h3>{mc.heading}</h3>
          <p>{mc.body}</p>
        </div>
        <div className="tsb-evt-pricing-member-actions">
          <a href={mc.ctaUrl} className="tsb-evt-btn tsb-evt-btn-coral">{mc.ctaLabel}</a>
          {mc.secondaryLabel && mc.secondaryUrl && (
            <a href={mc.secondaryUrl} className="tsb-evt-pricing-member-secondary">{mc.secondaryLabel}</a>
          )}
        </div>
        {mc.note && <p className="tsb-evt-pricing-member-note">{mc.note}</p>}
      </div>
    </div>
  );

  return (
    <section
      className={`tsb-evt-scope tsb-evt-section tint tsb-evt-pricing pricing-tiers${b.gradientTheme ? ' gradient-theme' : ''}`}
      id={b.anchorId}
    >

      <div className="tsb-evt-container">
        {(b.eyebrow || b.heading || b.subhead) && (
          <div style={{ textAlign: 'center', marginBottom: 0 }}>
            {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
            {b.heading && (
              <h2
                className="tsb-evt-section-heading"
                dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }}
              />
            )}
            {b.subhead && <p className="tsb-evt-section-intro">{b.subhead}</p>}
          </div>
        )}
      </div>

      {mcTop && memberBand}

      <div className="tsb-evt-pricing-grid">
        {(b.tiers ?? []).map((t) => {
          const paid = !!t.pricing && Object.keys(t.pricing).length > 0;
          const href = paid ? withCurrencyParam(t.ctaUrl, currency) : t.ctaUrl;
          return (
            <div key={t.key} className={`tsb-evt-pricing-card${t.highlight ? ' is-highlight' : ''}`}>
              {t.ribbon && <span className="tsb-evt-pricing-ribbon">{t.ribbon}</span>}
              {t.imageUrl && (
                <img className="tsb-evt-pricing-image" src={t.imageUrl} alt="" aria-hidden="true" />
              )}
              <h3 className="tsb-evt-pricing-title">{t.title}</h3>
              {renderPrice(t)}
              {renderDeltaNote(t)}
              {t.features && t.features.length > 0 && (
                <ul className="tsb-evt-pricing-features">
                  {t.features.map((f, i) => (
                    <li key={i}>
                      <span className="tsb-evt-pricing-check" aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              <a
                href={href}
                className={`tsb-evt-pricing-cta${t.highlight ? ' primary' : !paid ? ' free' : ''}`}
              >
                {t.ctaLabel}
              </a>
            </div>
          );
        })}
      </div>

      {b.footnote && (
        <div className="tsb-evt-container">
          <p className="tsb-evt-pricing-footnote">{b.footnote}</p>
        </div>
      )}

      {!mcTop && memberBand}
    </section>
  );
}

function InlineWaitlistFormRender({ b }: { b: InlineWaitlistFormBlock }) {
  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Mock submit until real wiring lands. Phase 3 of Path A will hit the
    // /api/embed/register endpoint in waitlist mode (programs/cohorts use the
    // existing widget snippet today; this is a thin native-block wrapper).
    alert('(demo) Waitlist signup wired in real implementation.');
  }
  return (
    <div className={`tsb-evt-scope tsb-evt-waitlist-wrap theme-${b.theme ?? 'light'}`}>
      <form className="tsb-evt-waitlist-form" onSubmit={onSubmit}>
        {b.formLabel && <div className="tsb-evt-waitlist-label">{b.formLabel}</div>}
        <div className="tsb-evt-waitlist-fields">
          <input type="text" className="tsb-evt-waitlist-input" placeholder={b.firstNamePlaceholder ?? 'First name'} />
          <input type="email" className="tsb-evt-waitlist-input" placeholder={b.emailPlaceholder ?? 'Email'} required />
        </div>
        <button type="submit" className="tsb-evt-btn tsb-evt-btn-coral" style={{ width: '100%', marginTop: 12 }}>
          {b.ctaLabel}
        </button>
        {b.trust && (
          <div
            className="tsb-evt-waitlist-trust"
            dangerouslySetInnerHTML={{ __html: formatTsbInline(b.trust) }}
          />
        )}
      </form>
    </div>
  );
}

// =========================================================================
// InlineRegistrationFormRender — engine-native React opt-in for events.
//
// Mirrors the embed widget's two-step flow:
//   1. POST /api/embed/register with { slug, email, firstName?, source }
//   2. If response carries needsLastName / needsCountry, render a step-2
//      prompt (mirroring landings/cbl-staging.html) and POST
//      /api/embed/register/complete with { token, lastName?, country? }
//   3. Navigate window.top.location.href to the resume URL so the engine
//      taker (gate → questions → result) takes over.
//
// Slug is derived from the page URL (`/take/<slug>` or `/events/<slug>`).
// Authoring is purely declarative — the block JSON only carries copy + theme.
// =========================================================================

const COUNTRY_OPTIONS = ['Australia', 'Canada', 'United States', 'United Kingdom', 'New Zealand', 'Other'];
const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? '';

function deriveSlugFromUrl(): string | null {
  try {
    const m = window.location.pathname.match(/\/(?:take|events)\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

function navigateTop(url: string): void {
  try {
    const top = window.top || window;
    top.location.href = url;
  } catch {
    window.location.href = url;
  }
}

interface RegStep1Response {
  ok?: boolean;
  redirectUrl?: string;
  resumeToken?: string;
  needsFirstName?: boolean;
  needsLastName?: boolean;
  needsCountry?: boolean;
  knownFirstName?: string;
  error?: string;
}

interface RegStep2Response {
  ok?: boolean;
  resumeUrl?: string;
  error?: string;
}

/** Read a small set of prefill params off the page URL on first render.
 *  Lets callers decorate a registration link (e.g. the replay page's
 *  "Join the next live session →" CTA, or an email blast) with
 *  `?email=...&firstName=...` to skip the typing. Accepts `utm_email`
 *  as an alias so marketing tools that standardize on the utm_ namespace
 *  work without a config flip. */
function readUrlPrefill(): { email: string; firstName: string } {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      email: (p.get('email') ?? p.get('utm_email') ?? '').trim(),
      firstName: (p.get('firstName') ?? p.get('first_name') ?? '').trim(),
    };
  } catch {
    return { email: '', firstName: '' };
  }
}

function InlineRegistrationFormRender({ b }: { b: InlineRegistrationFormBlock }) {
  const [step, setStep] = useState<'step1' | 'step2' | 'submitting'>('step1');
  const initialPrefill = readUrlPrefill();
  const [firstName, setFirstName] = useState(initialPrefill.firstName);
  const [email, setEmail] = useState(initialPrefill.email);
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Step-2 state populated from step-1 response.
  const [step2, setStep2] = useState<{
    token: string;
    redirectUrl: string;
    knownFirstName: string;
    needsFirstName: boolean;
    needsLastName: boolean;
    needsCountry: boolean;
  } | null>(null);

  const ctaLabel = b.ctaLabel || 'Save my seat — it’s free →';
  const loadingLabel = b.loadingLabel || 'Reserving your seat…';
  const theme = b.theme ?? 'dark';
  const source = b.source || 'engine-landing';

  async function handleStep1(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    const trimmedFirst = firstName.trim();
    // Email-first capture: step 1 asks for email only. First name (and last
    // name / country) are gathered on step 2 — and skipped entirely when the
    // contact record already has them (server returns needsFirstName=false).
    // A URL `?firstName=` prefill still flows through in the body below, so a
    // decorated link can let step 2 skip the name prompt too.
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }

    const slug = deriveSlugFromUrl();
    if (!slug) {
      setError('Couldn’t resolve the event from the URL. Please refresh and try again.');
      return;
    }

    setPending(true);
    try {
      const res = await fetch(`${API_BASE}/api/embed/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          email: trimmedEmail,
          firstName: trimmedFirst || undefined,
          source,
          attribution: getAttributionPayload(),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as RegStep1Response;
      if (!res.ok || !data.redirectUrl) {
        setError(data.error || 'Something went wrong. Please try again.');
        setPending(false);
        return;
      }

      const needsAny = !!data.needsFirstName || !!data.needsLastName || !!data.needsCountry;
      if (needsAny && data.resumeToken) {
        setStep2({
          token: data.resumeToken,
          redirectUrl: data.redirectUrl,
          knownFirstName: data.knownFirstName || trimmedFirst || '',
          needsFirstName: !!data.needsFirstName,
          needsLastName: !!data.needsLastName,
          needsCountry: !!data.needsCountry,
        });
        setStep('step2');
        setPending(false);
        return;
      }

      navigateTop(data.redirectUrl);
    } catch {
      setError('Network error. Please check your connection and try again.');
      setPending(false);
    }
  }

  async function handleStep2(e: React.FormEvent) {
    e.preventDefault();
    if (!step2) return;
    setError(null);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedCountry = country.trim();
    if (step2.needsFirstName && !trimmedFirst) {
      setError('Please enter your first name.');
      return;
    }
    if (step2.needsLastName && !trimmedLast) {
      setError('Please enter your last name.');
      return;
    }
    if (step2.needsCountry && !trimmedCountry) {
      setError('Please select your country, or skip below.');
      return;
    }

    setPending(true);
    try {
      const res = await fetch(`${API_BASE}/api/embed/register/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: step2.token,
          ...(trimmedFirst ? { firstName: trimmedFirst } : {}),
          ...(trimmedLast ? { lastName: trimmedLast } : {}),
          ...(trimmedCountry ? { country: trimmedCountry } : {}),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as RegStep2Response;
      if (!res.ok) {
        setError(data.error || 'Couldn’t save — please try again or skip below.');
        setPending(false);
        return;
      }
      navigateTop(data.resumeUrl || step2.redirectUrl);
    } catch {
      setError('Network error. You can skip below or try again.');
      setPending(false);
    }
  }

  function handleSkip() {
    if (!step2) return;
    navigateTop(step2.redirectUrl);
  }

  const wrapClass = `tsb-evt-scope tsb-evt-waitlist-wrap tsb-evt-reg-wrap theme-${theme}`;

  // Section-wrap mode: render the form inside a navy gradient final-section
  // bookend (mirrors the iframe LP's final-section). Triggered when `section`
  // is set on the block — heading + body + form + meta all together.
  if (b.section && theme === 'dark') {
    return (
      <section className="tsb-evt-scope tsb-evt-final tsb-evt-reg-final" id={b.anchorId}>
        <div className="tsb-evt-final-blobs" aria-hidden="true">
          <div /><div />
        </div>
        <div className="tsb-evt-final-inner" style={{ textAlign: 'center' }}>
          <h2 dangerouslySetInnerHTML={{ __html: formatTsbInline(b.section.heading) }} />
          {b.section.body && <p>{b.section.body}</p>}
          {b.section.body2 && <p style={{ marginTop: 18 }}>{b.section.body2}</p>}
          <div className={wrapClass}>
            {step === 'step1' && (
              <form className="tsb-evt-waitlist-form tsb-evt-reg-form" onSubmit={handleStep1} noValidate>
                {b.formLabel && <div className="tsb-evt-waitlist-label">{b.formLabel}</div>}
                <div className="tsb-evt-reg-stack">
                  <input
                    type="email"
                    className="tsb-evt-waitlist-input"
                    placeholder={b.emailPlaceholder ?? 'you@yourbookkeeping.com'}
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={pending}
                  />
                </div>
                {error && <div className="tsb-evt-reg-error" role="alert">{error}</div>}
                <button
                  type="submit"
                  className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-reg-submit"
                  disabled={pending}
                >
                  {pending ? loadingLabel : ctaLabel}
                </button>
                {b.trust && (
                  <div
                    className="tsb-evt-waitlist-trust"
                    dangerouslySetInnerHTML={{ __html: formatTsbInline(b.trust) }}
                  />
                )}
                {b.footnote && <div className="tsb-evt-reg-footnote">{b.footnote}</div>}
              </form>
            )}
            {step === 'step2' && step2 && (
              <form className="tsb-evt-waitlist-form tsb-evt-reg-form" onSubmit={handleStep2} noValidate>
                <div className="tsb-evt-reg-step2-heading">
                  {step2.needsFirstName && step2.needsLastName
                    ? "Almost there — what's your name?"
                    : step2.needsFirstName
                      ? "One more thing — what's your first name?"
                      : `One more thing, ${step2.knownFirstName?.trim() || 'there'} — ${
                          step2.needsLastName && step2.needsCountry
                            ? 'your last name and country?'
                            : step2.needsLastName
                              ? 'your last name?'
                              : 'where are you based?'
                        }`}
                </div>
                <div className="tsb-evt-reg-stack">
                  {step2.needsFirstName && (
                    <input
                      type="text"
                      className="tsb-evt-waitlist-input"
                      placeholder={b.firstNamePlaceholder ?? 'First name'}
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={pending}
                      autoFocus
                    />
                  )}
                  {step2.needsLastName && (
                    <input
                      type="text"
                      className="tsb-evt-waitlist-input"
                      placeholder="Last name"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={pending}
                      autoFocus={!step2.needsFirstName}
                    />
                  )}
                  {step2.needsCountry && (
                    <select
                      className="tsb-evt-waitlist-input"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={pending}
                      autoFocus={!step2.needsFirstName && !step2.needsLastName}
                    >
                      <option value="" disabled>Select your country…</option>
                      {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>
                {error && <div className="tsb-evt-reg-error" role="alert">{error}</div>}
                <button
                  type="submit"
                  className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-reg-submit"
                  disabled={pending}
                >
                  {pending ? 'Saving…' : 'Continue →'}
                </button>
                {!step2.needsFirstName && !step2.needsLastName && (
                  <button
                    type="button"
                    className="tsb-evt-reg-skip"
                    onClick={handleSkip}
                    disabled={pending}
                  >
                    Skip for now
                  </button>
                )}
              </form>
            )}
          </div>
          {b.section.meta && <div className="tsb-evt-final-meta">{b.section.meta}</div>}
        </div>
      </section>
    );
  }

  return (
    <div className={wrapClass} id={b.anchorId}>
      {step === 'step1' && (
        <form className="tsb-evt-waitlist-form tsb-evt-reg-form" onSubmit={handleStep1} noValidate>
          {b.formLabel && <div className="tsb-evt-waitlist-label">{b.formLabel}</div>}
          <div className="tsb-evt-reg-stack">
            <input
              type="email"
              className="tsb-evt-waitlist-input"
              placeholder={b.emailPlaceholder ?? 'you@yourbookkeeping.com'}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={pending}
            />
          </div>
          {error && <div className="tsb-evt-reg-error" role="alert">{error}</div>}
          <button
            type="submit"
            className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-reg-submit"
            disabled={pending}
          >
            {pending ? loadingLabel : ctaLabel}
          </button>
          {b.trust && (
            <div
              className="tsb-evt-waitlist-trust"
              dangerouslySetInnerHTML={{ __html: formatTsbInline(b.trust) }}
            />
          )}
          {b.footnote && <div className="tsb-evt-reg-footnote">{b.footnote}</div>}
        </form>
      )}

      {step === 'step2' && step2 && (
        <form className="tsb-evt-waitlist-form tsb-evt-reg-form" onSubmit={handleStep2} noValidate>
          <div className="tsb-evt-reg-step2-heading">
            {step2.needsFirstName && step2.needsLastName
              ? "Almost there — what's your name?"
              : step2.needsFirstName
                ? "One more thing — what's your first name?"
                : `One more thing, ${step2.knownFirstName?.trim() || 'there'} — ${
                    step2.needsLastName && step2.needsCountry
                      ? 'your last name and country?'
                      : step2.needsLastName
                        ? 'your last name?'
                        : 'where are you based?'
                  }`}
          </div>
          <div className="tsb-evt-reg-stack">
            {step2.needsFirstName && (
              <input
                type="text"
                className="tsb-evt-waitlist-input"
                placeholder={b.firstNamePlaceholder ?? 'First name'}
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={pending}
                autoFocus
              />
            )}
            {step2.needsLastName && (
              <input
                type="text"
                className="tsb-evt-waitlist-input"
                placeholder="Last name"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={pending}
                autoFocus={!step2.needsFirstName}
              />
            )}
            {step2.needsCountry && (
              <select
                className="tsb-evt-waitlist-input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={pending}
                autoFocus={!step2.needsFirstName && !step2.needsLastName}
              >
                <option value="" disabled>Select your country…</option>
                {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
          {error && <div className="tsb-evt-reg-error" role="alert">{error}</div>}
          <button
            type="submit"
            className="tsb-evt-btn tsb-evt-btn-coral tsb-evt-reg-submit"
            disabled={pending}
          >
            {pending ? 'Saving…' : 'Continue →'}
          </button>
          {!step2.needsFirstName && !step2.needsLastName && (
          <button
            type="button"
            className="tsb-evt-reg-skip"
            onClick={handleSkip}
            disabled={pending}
          >
            Skip for now
          </button>
          )}
        </form>
      )}
    </div>
  );
}

// =========================================================================
// Sticky white site nav — logo (left) + optional centered text strip + CTA
// (right). Mirrors iframe LP's site-nav. Sits above eventHero so the page
// starts with a polished sticky header. Renders nothing in admin preview's
// preview=intro mode if you want — kept simple for now.
// =========================================================================
function SiteNavRender({ b }: { b: SiteNavBlock }) {
  // Pull from BrandContext so future events drop in <siteNav/> with no props
  // and inherit logo + name + link from assessment.brand. Block props win
  // when set — author can still override per-event.
  const brand = useBrand();
  const phase = usePhase();
  const logoUrl = b.logoUrl ?? brand?.logoUrl ?? null;
  const logoHref = b.logoHref ?? brand?.logoLinkUrl ?? '#';
  const logoAlt = b.logoAlt ?? brand?.name ?? b.brandName ?? 'Home';
  const showWordmark = !logoUrl && !!b.brandName;

  // Phase-aware CTA: if ctaByPhase has an entry for the current phase, that
  // wins; explicit `null` hides the CTA on that phase; otherwise fall back
  // to block.cta. Phases not represented in ctaByPhase use the default.
  const phaseEntry =
    phase && b.ctaByPhase && phase in b.ctaByPhase
      ? b.ctaByPhase[phase as keyof NonNullable<typeof b.ctaByPhase>]
      : undefined;
  const resolvedCta = phaseEntry === null ? null : (phaseEntry ?? b.cta ?? null);
  // Only the top-level `cta` (not a ctaByPhase override) carries `action` in
  // its type — ctaByPhase entries are plain {label,url} and always navigate.
  const ctaAction = phaseEntry === undefined || phaseEntry === null ? b.cta?.action : undefined;
  const [modalOpen, setModalOpen] = useState(false);
  // See the comment at the render site: anchors only exist on the landing.
  const showNavLinks = !!b.navLinks?.length && (!phase || phase === 'intro');

  return (
    <nav className="tsb-evt-scope tsb-evt-site-nav">
      <div className="tsb-evt-site-nav-inner">
        <a
          href={logoHref}
          className="tsb-evt-site-logo"
          aria-label={logoAlt}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={logoAlt}
              className="tsb-evt-site-logo-mark is-image"
            />
          ) : (
            <span className="tsb-evt-site-logo-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-8.5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </span>
          )}
          {showWordmark && (
            <span className="tsb-evt-site-logo-words">
              <span className="tsb-evt-site-logo-name">{b.brandName}</span>
              {b.brandTag && <span className="tsb-evt-site-logo-tag">{b.brandTag}</span>}
            </span>
          )}
        </a>
        {b.centerText && (
          <div className="tsb-evt-site-nav-center">
            {b.showDot && <span className="tsb-evt-site-nav-dot" />}
            <span dangerouslySetInnerHTML={{ __html: formatTsbInline(b.centerText) }} />
          </div>
        )}
        {/* ★ Intro phase only. navLinks are in-page anchors to landing
            sections (#pricing, #proof, …). The same siteNav block renders as
            persistent chrome on the survey/gate/result phases, where those
            sections don't exist — showing the links there would give every
            registrant a row of links that scroll nowhere. */}
        {showNavLinks && (
          <div className="tsb-evt-nav-links">
            {b.navLinks!.map((link, i) => (
              <a key={i} href={link.href} className="tsb-evt-nav-link">{link.label}</a>
            ))}
          </div>
        )}
        {resolvedCta && (
          <a
            href={resolvedCta.url}
            className="tsb-evt-site-nav-cta"
            onClick={(e) => {
              if (ctaAction === 'register') {
                e.preventDefault();
                setModalOpen(true);
                return;
              }
              if (!resolvedCta.url.startsWith('#')) return;
              const id = resolvedCta.url.slice(1);
              if (!id) return;
              const el = document.getElementById(id);
              if (el && (el instanceof HTMLButtonElement || el instanceof HTMLAnchorElement)) {
                e.preventDefault();
                el.click();
              }
            }}
          >{resolvedCta.label}</a>
        )}
      </div>
      {modalOpen && (
        <LateRegisterModal
          mode="pre-event"
          heading={b.cta?.modalHeading}
          body={b.cta?.modalBody}
          submitLabel={b.cta?.modalSubmitLabel}
          onClose={() => setModalOpen(false)}
        />
      )}
    </nav>
  );
}

// =========================================================================
// addToCalendar — real Google link + .ics download (shared lib/calendar.ts).
// `variant: 'band'` folds the date/time/length strip and the buttons into one
// navy card so a confirmation page's calendar step reads as a single object.
// Renders nothing on a missing/invalid instant: never a broken button.
// =========================================================================
function AddToCalendarRender({ b }: { b: AddToCalendarBlock }) {
  const links = buildCalendarLinks({
    title: b.title,
    start: new Date(b.startIso),
    durationMinutes: b.durationMinutes,
    details: b.details,
    location: b.location,
  });
  if (!links) return null;
  const buttons = (
    <div className="tsb-evt-cal-actions">
      <a className="tsb-evt-cal-btn" href={links.googleHref} target="_blank" rel="noopener noreferrer">
        Google Calendar
      </a>
      <a className="tsb-evt-cal-btn" href={links.icsHref} download={`${b.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`}>
        Apple / Outlook (.ics)
      </a>
    </div>
  );
  if (b.variant === 'band') {
    return (
      <section className="tsb-evt-scope tsb-evt-cal-band">
        {b.strip && b.strip.length > 0 && (
          <div className="tsb-evt-cal-strip">
            {b.strip.map((it, i) => (
              <div key={i} className="tsb-evt-cal-part">
                <div className="tsb-evt-cal-lbl">{it.label}</div>
                <div className="tsb-evt-cal-val">{it.value}</div>
              </div>
            ))}
          </div>
        )}
        <div className="tsb-evt-cal-side">
          {buttons}
          {b.note && <div className="tsb-evt-cal-note">{b.note}</div>}
        </div>
      </section>
    );
  }
  return (
    <div className="tsb-evt-scope tsb-evt-cal">
      {buttons}
      {b.note && <div className="tsb-evt-cal-note">{b.note}</div>}
    </div>
  );
}

// =========================================================================
// beltLadder — the six TSB Belt stages from lib/belt.ts (the ONE display
// ladder; never a second list here) plus an optional example-result card
// that previews the gap diagnosis before the registrant has answered.
// =========================================================================
function BeltLadderRender({ b }: { b: BeltLadderBlock }) {
  const ex = b.example;
  const beltName = (key: string, fallback?: string) =>
    fallback ?? BELTS.find(x => x.key === key)?.name ?? key;
  return (
    <section className="tsb-evt-scope tsb-evt-belt">
      {(b.eyebrow || b.heading || b.lead) && (
        <div className="tsb-evt-belt-head">
          {b.eyebrow && <div className="tsb-evt-eyebrow">{b.eyebrow}</div>}
          {b.heading && (
            <h2 className="tsb-evt-section-heading" dangerouslySetInnerHTML={{ __html: formatTsbInline(b.heading) }} />
          )}
          {b.lead && (
            <p className="tsb-evt-belt-lead" dangerouslySetInnerHTML={{ __html: formatTsbInline(b.lead) }} />
          )}
        </div>
      )}
      <div className="tsb-evt-belt-ladder" role="list" aria-label="The six Belt stages">
        {BELTS.map(belt => (
          <div key={belt.key} className={`tsb-evt-belt-rung is-${belt.key}`} role="listitem">
            <div className="tsb-evt-belt-band" />
            <div className="tsb-evt-belt-name">{belt.name.replace(/ Belt$/, '')}</div>
            <div className="tsb-evt-belt-stage">{belt.stage}</div>
          </div>
        ))}
      </div>
      {b.caption && <p className="tsb-evt-belt-caption">{b.caption}</p>}
      {ex && (
        <div className="tsb-evt-belt-example" aria-label="Example result">
          {ex.label && <div className="tsb-evt-belt-ex-label">{ex.label}</div>}
          <div className="tsb-evt-belt-ex-k">Your revenue says</div>
          <div className="tsb-evt-belt-ex-v"><span className={`tsb-evt-belt-chip is-${ex.ceilingKey}`} />{beltName(ex.ceilingKey, ex.ceilingLabel)}</div>
          <div className="tsb-evt-belt-ex-k">Your calendar says</div>
          <div className="tsb-evt-belt-ex-v"><span className={`tsb-evt-belt-chip is-${ex.floorKey}`} />{beltName(ex.floorKey, ex.floorLabel)}</div>
          <div className="tsb-evt-belt-ex-say">{ex.text}</div>
        </div>
      )}
    </section>
  );
}

// =========================================================================
// reasonsList — "Why this / Why now / Why belts" labelled rows. Links are real
// anchors (paragraph text never parses markdown — see sop-ee-belt-assessment).
// =========================================================================
function ReasonsListRender({ b }: { b: ReasonsListBlock }) {
  return (
    <div className="tsb-evt-scope tsb-evt-reasons">
      {b.items.map((it, i) => (
        <div key={i} className="tsb-evt-reason">
          <div className="tsb-evt-reason-label">{it.label}</div>
          <p className="tsb-evt-reason-body">
            {it.lead && <strong>{it.lead} </strong>}
            {it.body}
            {it.linkLabel && it.linkUrl && (
              <>
                {' '}
                <a className="tsb-evt-reason-link" href={it.linkUrl} target="_blank" rel="noopener noreferrer">{it.linkLabel}</a>
              </>
            )}
          </p>
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// eventFooter — the standard event-page footer (Copyright + Privacy/Terms/
// Contact). See EventFooterBlock in types.ts for why this replaced a
// hand-typed `html` block per event. Links come from PolicyLinks.tsx's
// POLICY_LINKS/CONTACT_URL — the same canonical source every other public EE
// page uses — so there is no per-page copy of these URLs left to drift.
// =========================================================================
function EventFooterRender({ b }: { b: EventFooterBlock }) {
  const variant = b.variant ?? 'navy';
  const year = new Date().getFullYear();
  return (
    <footer className={`block-event-footer tsb-evt-footer tsb-evt-footer--${variant}`}>
      <div className="tsb-evt-footer-row">
        <span>&copy; {year} The Successful Bookkeeper Global Inc.</span>
        <span className="tsb-evt-footer-sep" aria-hidden="true">&middot;</span>
        <a href={POLICY_LINKS.privacy} target="_blank" rel="noopener noreferrer">Privacy</a>
        <span className="tsb-evt-footer-sep" aria-hidden="true">&middot;</span>
        <a href={POLICY_LINKS.terms} target="_blank" rel="noopener noreferrer">Terms</a>
        <span className="tsb-evt-footer-sep" aria-hidden="true">&middot;</span>
        <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">Contact</a>
      </div>
    </footer>
  );
}
